import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import CommonLayout from '../../layout/CommonLayout';
import {
  addStoryPoints,
  getRoleRatesAndStoryPoints,
  addCapacity,
  getProjectList,
  getHolidayList,
  getId,
  getBoardList,
  getProjectManagementData,
  APP_STRINGS,
  getSprintList,
  getReleaseDetails,
} from '../../constants';
import DropdownButton from '../Common/DropDown';
import { CommonFunction } from '../../utils/commonFunctions';
import { 
  storeBoardInSession, 
  restoreBoardFromSession, 
  clearBoardFromSession,
  computeProjectDisplayName
} from '../../utils/boardUtils';
// import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import Modal from '../Common/Modal';
import AddCapacityUsersModal from './AddCapacityUsersModal';
import { useNavigate } from 'react-router-dom';
import AGrid from '../Common/AGgrid';
import { capacityPlanningColumns } from '../AGColumns/CapacityPlanningColumns';
import { hasPrivilege, PRIVILEGES } from '../../utils/permissions';
// import { getAllOrgsListAPI } from '../../constants';
import { 
  setSelectedTypeValue,
  setProjectList,
  setBoardListForProject,
  setHolidayListForCompany,
  setRoleRates,
  setStoryPointRatio,
  setUserList,
  setUserListForProject,
  setJiraTableData,
  setSprintList,
  setReleasesList,
  setSprint,
  setRelease,
} from '../../store/JiraSlices/jiraSlice';
import Spinner from '../Common/Spinner';
import '../../assets/css/global.scss';
import { useSelector } from 'react-redux';
import { getBoardLabels } from '../../utils/boardUtils';
import {
  filterAssigneesForCapacityPlanView,
  isAssigneeOnCapacityPlan,
  resolveCapacityPresentInPlan,
  assigneeHasSyncedAllocation,
  isBucketAssigneeName,
  isUnassignedBucketName,
  isManuallyAddedCapacityRow,
  isSprintOrReleaseLockedRow,
  shouldShowUnassignedCapacityApiAssignee,
  shouldShowUnassignedCapacityGridRow,
  hasValidCapacityAssigneeRows,
  mergeCapacityAssigneesWithUserList,
  buildStandupCapacityAssigneesFallback,
  applySavedCapacityAvailabilityToAssignees,
  mergeLocalCapacityAvailabilityOverApi,
  buildIssueEstimateTotalsByAssignee,
  buildIssueTicketCountsByAssignee,
  isValidCapacityEmail,
  resolveStandupHoursBasedProject,
  sortStandupSprintDropdownList,
  resolveAddUserModalLockedForSave,
  applyCarryForwardCapacityPlanToAssignees,
  applyCarryForwardActivePlanToFuture,
  unionMissingCarryForwardAssignees,
  resolvePriorSprintPlanSource,
  resolvePriorReleasePlanSource,
  isUnreleasedRelease,
  isLiveReleasePlanSource,
  isSequentialReleaseLiveCarryTarget,
  assigneeHasScopeTickets,
  hasSavedCapacitySelectionPlan,
} from '../../utils/capacityPlanningUtils';

const getUniqueId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** Capacity Planning grid only — room for bordered email field + inline error. */
const CAPACITY_PLANNING_ROW_HEIGHT = 42;

const normCapacityName = (n) => String(n || '').trim().toLowerCase();

const getCapacityEmailStorageKey = ({ projectId, mode, dataKey }) =>
  `capacity-dashboard-emails:${projectId}:${mode}:${dataKey}`;

const readCapacityEmailMap = ({ projectId, mode, dataKey }) => {
  if (typeof window === 'undefined' || !projectId || !mode || !dataKey) return {};
  try {
    const raw = window.localStorage.getItem(
      getCapacityEmailStorageKey({ projectId, mode, dataKey }),
    );
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.warn('Could not read saved capacity emails:', error);
    return {};
  }
};

const getStoredCapacityEmail = (emailMap, assigneeName) => {
  const key = normCapacityName(assigneeName);
  if (!key) return '';
  return Object.prototype.hasOwnProperty.call(emailMap || {}, key)
    ? String(emailMap[key] || '').trim()
    : '';
};

const persistCapacityEmailMap = ({ projectId, mode, dataKey, rows = [] }) => {
  if (typeof window === 'undefined' || !projectId || !mode || !dataKey) return;
  const emailMap = {};
  for (const row of rows) {
    const name = String(row?.name ?? row?.assignee ?? '').trim();
    if (!name) continue;
    emailMap[normCapacityName(name)] = String(row?.email || '').trim();
  }
  try {
    window.localStorage.setItem(
      getCapacityEmailStorageKey({ projectId, mode, dataKey }),
      JSON.stringify(emailMap),
    );
  } catch (error) {
    console.warn('Could not persist capacity emails:', error);
  }
};

const CapacityDashboard = () => {
  const [teamData, setTeamData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [storyPointToHourRatio, setStoryPointToHourRatio] = useState(8);
  const theme = useSelector((state) => state.theme.theme);
  const [saveStatus, setSaveStatus] = useState(false);
  const invalidEmailRowIdsRef = useRef(new Set());
  const [emailValidationRevision, setEmailValidationRevision] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isHoursBasedProject, setIsHoursBasedProject] = useState(false);
  const projectRef = useRef(null);
  const dispatch = useDispatch();
  const [selectedSprint, setSelectedSprint] = useState({ id: '', name: '' });
  const [selectedProject, setSelectedProject] = useState({ id: '', name: '' });
  const [selectedRelease, setSelectedRelease] = useState({ id: '', releaseName: '' });
  const [isProjectOpen, setIsProjectOpen] = useState(false);
  const [isSprintOpen, setIsSprintOpen] = useState(false);
  const [getAllProjectList, setGetAllProjectList] = useState([]);
  const [getAllOrgsList, setGetAllOrgsList] = useState([]);
  const [getAllSprintList, setGetAllSprintList] = useState([]);
  const capacitySprintDropdownList = useMemo(
    () => sortStandupSprintDropdownList(Array.isArray(getAllSprintList) ? getAllSprintList : []),
    [getAllSprintList],
  );
  const [isValueOpen, setIsValueOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState({
    label: APP_STRINGS.SELECT_AN_OPTION,
    value: '',
  });
  const [getAllReleaseList, setGetAllReleaseList] = useState([]);
  const organizationRef = useRef(null);
  const [selectedOrg, setSelectedOrg] = useState({ id: '', name: '' });
  const [isOrganizationOpen, setIsOrganizationOpen] = useState(false); 
  const [warningModal, setWarningModal] = useState({isOpen: false, message: ''});
  const [isAddUsersModalOpen, setIsAddUsersModalOpen] = useState(false);
  const [addModalSprintReleaseAssignees, setAddModalSprintReleaseAssignees] = useState([]);
  const [modalRecalledOffPlan, setModalRecalledOffPlan] = useState([]);
  const [manualDeleteConfirmRowId, setManualDeleteConfirmRowId] = useState(null);
  const [purgedOffPlanManualNorms, setPurgedOffPlanManualNorms] = useState([]);
  const [isBoardOpen, setIsBoardOpen] = useState(false);
  const [projectBoardCount, setProjectBoardCount] = useState({});
  const [subMenuBoards, setSubMenuBoards] = useState([]);
  const [subMenuPosition, setSubMenuPosition] = useState({ top: 0, left: 0 });
  const [currentProjectForBoard, setCurrentProjectForBoard] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState({ id: '', name: '', type: '' });
   const [pageSize, setPageSize] = useState(7);
   const [gridHeight, setGridHeight] = useState("58vh");
  const sprintRef = useRef(null);
  const valueRef = useRef(null);
  const [, setType] = useState(APP_STRINGS.VALUE_SPRINT);
  const navigate = useNavigate();
  const gridApiRef = useRef(null);
  const changingRef = useRef(false);
  const rolesRef = useRef([]);
  const roleRatesFetchPromiseRef = useRef(null);
  const holidayFetchPromiseRef = useRef(null);
  const userListFetchPromiseRef = useRef({});
  const pendingBoardValidationRef = useRef(null);
  const fetchProjectDataRef = useRef(null);
  const preserveSavedAvailabilityRef = useRef(null);
  const loadedCapacityScopeKeyRef = useRef('');
  const capacityIssueCountRef = useRef(null);
  const teamDataRef = useRef([]);
  /** Sprint|release assignees with presentInPlan no (hidden from grid); merged into Save Data payload so they are not dropped on save/sync. */
  const offPlanManualAssigneesRef = useRef([]);
  const isFutureScopeRef = useRef(false);
  const locallyEditedNormsRef = useRef(new Set());
  const loadedPlanLockedByNormRef = useRef(new Map());
  const loadedModifiedByNormRef = useRef(new Map());
  const jiraData = useSelector((state) => state.jira || {});
  const sprintLength = jiraData?.sprintLength || null;
  const totalDays = sprintLength?.sprintLengthInDays ?? 0;
  const { sprintLabel, releaseLabel, isAzure, isClickupList,isGithubIssues } = getBoardLabels({
    selectedBoard,
    projectList: getAllProjectList,
    selectedProjectId: selectedProject.id,
  });

  const {
    handleProject,
    handleSprint,
    handleRelease,
    handleValue,
    handleOrganization,
  } = CommonFunction();

  useEffect(() => { rolesRef.current = roles; }, [roles]);
  useEffect(() => {
    teamDataRef.current = teamData;
  }, [teamData]);
  const recallContextKeyRef = useRef('');
  useEffect(() => {
    const mode = String(selectedValue?.value || '').toLowerCase();
    const isSprint = mode === 'sprint';
    const isRelease = mode === 'release';
    const sid = isSprint ? sessionStorage.getItem('sprintId') || '' : '';
    const rid = isRelease ? sessionStorage.getItem('releaseId') || '' : '';
    const key = `${isSprint ? 's' : isRelease ? 'r' : 'n'}:${sid || rid}`;
    if (recallContextKeyRef.current && recallContextKeyRef.current !== key) {
      setModalRecalledOffPlan([]);
      setPurgedOffPlanManualNorms([]);
      locallyEditedNormsRef.current = new Set();
    }
    recallContextKeyRef.current = key;
  }, [selectedSprint.id, selectedRelease.id, selectedValue.value]);

  const orgsData = useSelector((state) => state.jira?.organizationList);

  const canViewBillingRate = hasPrivilege(PRIVILEGES.BILLING_RATE);

  const allColumns = useMemo(
    () => capacityPlanningColumns(!isHoursBasedProject, theme, selectedValue?.value, isGithubIssues),
    [isHoursBasedProject, theme, selectedValue?.value, isGithubIssues],
  );

  const handleOrganizationChange = async (value) => {
    try {
      await handleOrganization(value, dispatch);
    } catch (error) {
      console.error('Error handling project selection:', error);
    }
  };

  const handleValueChange = (value) => {
    handleValue(value, dispatch);
    setType(value.value);
    setSelectedValue(value);
    dispatch(setSelectedTypeValue({ selectedValueLabel: value.label, selectedValue: value.value }));
    setIsValueOpen(false);
    if (value !== 'sprint') {
      setSelectedSprint({ id: '', name: '' });
    }
  };

  const handleProjectChange = async (value) => {
    try {
      setSelectedBoard({ id: '', name: '', type: '' });
      clearBoardFromSession();

      const companyId = getId().companyId;
      const boards = await fetchBoardList(companyId, value);
      const firstBoard = boards[0];
      const boardId = firstBoard?.id || firstBoard?._id || '';
      const boardType = firstBoard?.type || firstBoard?.boardType || '';

      if (boardId) {
        setSelectedBoard({
          id: boardId,
          name: firstBoard?.name || firstBoard?.boardName || '',
          type: boardType,
        });
        sessionStorage.setItem('boardId', boardId);
        storeBoardInSession(
          boardId,
          firstBoard?.name || firstBoard?.boardName || '',
          boardType,
        );
      }

      await handleProject(value, boardType, dispatch);
      await fetchProjectDataRef.current?.();
    } catch (error) {
      console.error('Error handling project selection:', error);
    }
  };

  const fetchBoardList = async (companyId, projectId) => {
    try {
      const cachedBoardsByProject = jiraData?.boardListByProjectId || {};
      const cachedBoards = cachedBoardsByProject[projectId];
      if (cachedBoards?.length > 0) {
        return cachedBoards;
      }
      const response = await getBoardList(companyId, projectId);
      let boards = [];
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          boards = response.data;
        } else if (response.data.boards && Array.isArray(response.data.boards)) {
          boards = response.data.boards;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          boards = response.data.data;
        }
      }
      dispatch(setBoardListForProject({ projectId, boards }));
      return boards;
    } catch (error) {
      console.error('Error fetching board list:', error);
      return [];
    }
  };

  const handleBoardChange = async (boardId, projectId) => {
    try {
      const selectedBoardData = subMenuBoards.find(board => (board.id || board._id) === boardId);
      if (selectedBoardData) {
        setSelectedBoard({
          id: boardId,
          name: selectedBoardData.name || selectedBoardData.boardName || '',
          type: selectedBoardData.type || selectedBoardData.boardType || ''
        });
        sessionStorage.setItem('boardId', boardId);
        storeBoardInSession(boardId, selectedBoardData.name || selectedBoardData.boardName || '', selectedBoardData.type || selectedBoardData.boardType || '');
        await handleProject(projectId, selectedBoardData.type || selectedBoardData.boardType, dispatch);
        const project = getAllProjectList.find(p => p._id === projectId);
        setSelectedProject({
          id: projectId,
          name: project?.name || ''
        });
        await fetchProjectDataRef.current?.();
        setSubMenuBoards([]);
        setCurrentProjectForBoard('');
        setIsBoardOpen(false);
      }
    } catch (error) {
      console.error('Error handling board selection:', error);
    }
  };

  const handleProjectHover = async (projectId) => {
    try {
      setIsBoardOpen(false);
      setSubMenuBoards([]);
      setCurrentProjectForBoard('');
      await new Promise(resolve => setTimeout(resolve, 50));
      const companyId = getId().companyId;
      const boards = await fetchBoardList(companyId, projectId);
      if (boards.length > 1) {
        const hoveredElement = document.querySelector(`[data-project-id="${projectId}"]`);
        if (hoveredElement) {
          const rect = hoveredElement.getBoundingClientRect();
          setSubMenuPosition({
            top: rect.top,
            left: rect.right + 10 // Add small spacing to match StandUp page visual gap
          });
        } else {
          setSubMenuPosition({
            top: 100,
            left: 420
          });
        }
        
        setSubMenuBoards(boards);
        setCurrentProjectForBoard(projectId);
        setIsBoardOpen(true);
      } else {
        setIsBoardOpen(false);
        setSubMenuBoards([]);
        setCurrentProjectForBoard('');
      }
    } catch (error) {
      console.error('Error in handleProjectHover:', error);
      setIsBoardOpen(false);
      setSubMenuBoards([]);
      setCurrentProjectForBoard('');
    }
  };

  const handleProjectMouseLeave = () => {
    setTimeout(() => {
      const submenuElement = document.querySelector('.board-submenu');
      const allProjectElements = document.querySelectorAll('[data-project-id]');
      let isHoveringOverProject = false;
      allProjectElements.forEach(element => {
        if (element.matches(':hover')) {
          isHoveringOverProject = true;
        }
      });
      
      if (submenuElement && !submenuElement.matches(':hover') && !isHoveringOverProject) {
        setIsBoardOpen(false);
        setSubMenuBoards([]);
        setCurrentProjectForBoard('');
      }
    }, 150); 
  };

 
  const handleSprintChange = async (value) => {
    changingRef.current = true;
    try {
      await handleSprint(value, dispatch);
    } catch (error) {
      console.error('Error handling project selection:', error);
    }
    const selectedSprint = getAllSprintList.find((sprint) => sprint._id === value);
    sessionStorage.setItem('sprintId', selectedSprint._id);
    setSelectedSprint({ id: selectedSprint._id, name: selectedSprint.name });
    setType(APP_STRINGS.VALUE_SPRINT);
    preserveSavedAvailabilityRef.current = null;
    loadedCapacityScopeKeyRef.current = '';
    await fetchProjectDataRef.current?.({ forceRefresh: true });
    setTimeout(() => {
      changingRef.current = false;
    }, 300);
  };

  const handleReleaseChange = async (value) => {
    changingRef.current = true;
    try {
      await handleRelease(value, dispatch);
    } catch (error) {
      console.error('Error handling project selection:', error);
    }
    const releaseSelected = getAllReleaseList.find((release) => release._id === value);
    sessionStorage.setItem('releaseId', releaseSelected._id);
    setSelectedRelease({ id: releaseSelected._id, releaseName: releaseSelected.releaseName });
    setIsSprintOpen(false);
    setType(APP_STRINGS.VALUE_RELEASE);
    preserveSavedAvailabilityRef.current = null;
    loadedCapacityScopeKeyRef.current = '';
    await fetchProjectDataRef.current?.({ forceRefresh: true });
    setTimeout(() => {
      changingRef.current = false;
    }, 300);
  };

  const calculateDependentValues = useCallback(
    (row, projectIsHoursBased = isHoursBasedProject) => {
      const toNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      };

      const allocatedStoryPoints = Math.max(toNum(row.allocatedStoryPoints), 0);
      const allocatedHours = Math.max(toNum(row.allocatedHours), 0);
      const leaves = Math.max(toNum(row.leaves), 0);
      const holiday = Math.max(toNum(row.holiday), 0);
      const sprintLength = Number(totalDays || 0);
      const availableDays = Math.max(sprintLength - leaves - holiday, 0);
      const availableHours = Math.max(toNum(row.availableHours), 0);

      const totalOffDays = holiday + leaves;
      let netAvailableCapacity = availableHours;

      if (projectIsHoursBased) {
        // Hours-based projects: net = availableHours - (holiday+leaves)*9
        netAvailableCapacity = Math.max(availableHours - totalOffDays * 9, 0);
      } else if (availableHours !== 0) {
        // Keep existing behavior for story-point/estimate based projects.
        netAvailableCapacity = Math.max(availableHours - totalOffDays * 1, 0);
      }

      const billingRate = Math.max(toNum(row.billingRate), 0);
      let remainingCapacity, overage, workload, totalBilling;
      if (projectIsHoursBased) {
        remainingCapacity = netAvailableCapacity - allocatedHours;
        overage = allocatedHours - netAvailableCapacity;
        workload =
          netAvailableCapacity > 0 ? Math.min((allocatedHours / netAvailableCapacity) * 100) : 0;
        totalBilling = allocatedHours * billingRate;
      } else {
        remainingCapacity = netAvailableCapacity - allocatedStoryPoints;
        overage = allocatedStoryPoints - netAvailableCapacity;
        workload =
          netAvailableCapacity > 0
            ? Math.min((allocatedStoryPoints / netAvailableCapacity) * 100)
            : 0;
        const hoursForBilling = allocatedStoryPoints * (storyPointToHourRatio || 8);
        totalBilling = hoursForBilling * billingRate;
      }
      return {
        ...row,
        availableDays,
        availableHours: parseFloat(availableHours.toFixed(2)),
        sprintLength,
        allocatedHours: parseFloat(allocatedHours.toFixed(2)),
        allocatedStoryPoints: parseFloat(allocatedStoryPoints.toFixed(2)),
        initialPlannedAllocation:
          row.initialPlannedAllocation !== null
          && row.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(row.initialPlannedAllocation))
            ? parseFloat(Number(row.initialPlannedAllocation).toFixed(2))
            : row.initialPlannedAllocation,
        remainingCapacity: parseFloat(remainingCapacity.toFixed(2)),
        overage: parseFloat(overage.toFixed(2)),
        totalBilling: parseFloat(totalBilling.toFixed(2)),
        workload: parseFloat(workload.toFixed(2)),
        netAvailableCapacity: parseFloat(netAvailableCapacity.toFixed(2)),
      };
    },
    [totalDays, storyPointToHourRatio, isHoursBasedProject],
  );

  const getHolidaysInSprint = (holidays, sprintStart, sprintEnd) => {
    const start = new Date(sprintStart);
    const end = new Date(sprintEnd);
    const sprintHolidays = holidays.filter((h) => {
      const holidayDate = new Date(h.date);
      return holidayDate >= start && holidayDate <= end;
    });
    const uniqueHolidayDates = Array.from(
      new Set(sprintHolidays.map((h) => new Date(h.date).toISOString().split('T')[0])),
    );
    return uniqueHolidayDates;
  };

  const updateTeamDataWithUserData = useCallback(
    (project, holidays = []) => {
      if (project && project.assignees && project.assignees.length > 0) {
        const projectIsHoursBased = project.hours || false;
        const sprintHolidays = getHolidaysInSprint(holidays, project.startDate, project.endDate);
        const assigneesForGrid = filterAssigneesForCapacityPlanView(
          project.assignees,
          projectIsHoursBased,
        );
        const jiraOrdered = [];
        const manualOrdered = [];
        for (const a of assigneesForGrid) {
          if (isManuallyAddedCapacityRow(a)) {
            manualOrdered.push(a);
          } else {
            jiraOrdered.push(a);
          }
        }
        const sortJira = (arr) => {
          const locked = arr.filter((x) => isSprintOrReleaseLockedRow(x));
          const rest = arr.filter((x) => !isSprintOrReleaseLockedRow(x));
          return [...locked, ...rest];
        };
        const orderedAssignees = [...sortJira(jiraOrdered), ...manualOrdered];
        const emptyCapacityRow = () => ({
          id: getUniqueId(),
          name: '',
          email: '',
          role: '',
          allocationType: '',
          availableHours: 0,
          netAvailableCapacity: 0,
          previousAvailableDays: 0,
          previousStoryPointRatio: storyPointToHourRatio,
          allocatedStoryPoints: 0,
          allocatedHours: 0,
          billingRate: 0,
          leaves: 0,
          holiday: 0,
          availableDays: 0,
          isNew: true,
          manuallyAdded: false,
        });
        if (orderedAssignees.length === 0) {
          setTeamData([emptyCapacityRow()]);
        } else {
          const updatedTeamData = orderedAssignees.map((assignee) => {
            const holidayCount = sprintHolidays.length;
            const leaveCount = assignee.leaves || 0;
            const latestRole = rolesRef.current.find((r) => r.role === assignee.role);
            const billingRate = latestRole ? latestRole.rate : (assignee.billingRate || 0);
            const manuallyAdded = isManuallyAddedCapacityRow(assignee);
            const addedManuallyFlag =
              assignee.addedManually === 'yes' || assignee.addedManually === true ? 'yes' : 'no';
            const sprintOrReleaseUser =
              assignee.sprintOrReleaseUser === 'yes' || assignee.sprintOrReleaseUser === true
                ? 'yes'
                : 'no';
            const mappedData = {
              id: assignee._id != null ? String(assignee._id) : getUniqueId(),
              name: assignee.assignee,
              email: String(assignee.email || '').trim(),
              role: assignee.role,
              allocationType:
                isUnassignedBucketName(assignee.assignee) &&
                String(assignee.allocationType || '').trim().toLowerCase() === 'auto'
                  ? ''
                  : (assignee.allocationType || ''),
              availableHours: Number(assignee.availableHours ?? 0),
              allocatedStoryPoints: projectIsHoursBased
                ? 0
                : Number(assignee.allocatedStoryPoints ?? assignee.allocatedHours ?? 0),
              allocatedHours: projectIsHoursBased ? assignee.allocatedHours || 0 : 0,
              tickets: Number(assignee.tickets || 0),
              initialPlannedAllocation:
                assignee.initialPlannedAllocation !== null
                && assignee.initialPlannedAllocation !== undefined
                && Number.isFinite(Number(assignee.initialPlannedAllocation))
                  ? Number(assignee.initialPlannedAllocation)
                  : undefined,
              billingRate,
              leaves: leaveCount || 0,
              holiday: holidayCount,
              isNew: false,
              manuallyAdded,
              addedManually: manuallyAdded ? 'yes' : addedManuallyFlag,
              sprintOrReleaseUser,
              presentInPlan: resolveCapacityPresentInPlan(assignee, projectIsHoursBased),
              addUserModalLocked:
                Number(assignee.tickets || 0) > 0 || assigneeHasScopeTickets(assignee)
                  ? 'yes'
                  : 'no',
              modified: assignee.modified === true,
            };
            // Single source of truth for net capacity (hours-based uses (holiday+leaves)*9).
            return calculateDependentValues(mappedData, projectIsHoursBased);
          });
          setTeamData(updatedTeamData);
        }
      } else {
        setTeamData([
          {
            id: getUniqueId(),
            name: '',
            email: '',
            role: '',
            allocationType: '',
            availableHours: 0,
            netAvailableCapacity: 0,
            previousAvailableDays: 0,
            previousStoryPointRatio: storyPointToHourRatio,
            allocatedStoryPoints: 0,
            allocatedHours: 0,
            billingRate: 0,
            leaves: 0,
            holiday: 0,
            availableDays: 0,
            isNew: true,
            manuallyAdded: false,
          },
        ]);
      }
    },
    [calculateDependentValues, storyPointToHourRatio],
  );

  const fetchProjectData = useCallback(async () => {
    setLoading(true);
    try {
      const sprintId = sessionStorage.getItem('sprintId');
      const releaseId = sessionStorage.getItem('releaseId');
      const rawCapacityMode =
        selectedValue?.value || jiraData?.selectedValue || sessionStorage.getItem('typeValue') || '';
      const capacityMode = String(rawCapacityMode).toLowerCase();
      const isSprintCapacity = capacityMode === 'sprint';
      const isReleaseCapacity = capacityMode === 'release';
      const selectedProjectId = sessionStorage.getItem('projectId') || '';
      const dataKeyForEmails = isSprintCapacity ? sprintId : isReleaseCapacity ? releaseId : '';
      const scopeKey = `${capacityMode}:${dataKeyForEmails || ''}`;
      const sameScopeAsLoadedGrid = loadedCapacityScopeKeyRef.current === scopeKey;
      const savedEmailByName = readCapacityEmailMap({
        projectId: selectedProjectId,
        mode: capacityMode,
        dataKey: dataKeyForEmails,
      });
      let startDate = null;
      let endDate = null;
      const companyId = getId().companyId;
      const holidayPromise = (async () => {
        try {
          const cachedHolidays = jiraData?.holidayListByCompanyId?.[companyId];
          if (Array.isArray(cachedHolidays)) {
            return cachedHolidays;
          }
          if (!holidayFetchPromiseRef.current) {
            holidayFetchPromiseRef.current = (async () => {
              const holidayData = await getHolidayList(companyId);
              const fetchedHolidays = holidayData?.holidayList || [];
              dispatch(setHolidayListForCompany({ companyId, holidays: fetchedHolidays }));
              return fetchedHolidays;
            })()
              .catch((holidayError) => {
                console.warn('Could not load holiday list:', holidayError);
                return [];
              })
              .finally(() => {
                holidayFetchPromiseRef.current = null;
              });
          }
          return await holidayFetchPromiseRef.current;
        } catch (holidayError) {
          console.warn('Could not load holiday list:', holidayError);
          return [];
        }
      })();
      const normalizeAssigneeName = (name) => String(name || '').trim().toLowerCase();
      const normalizeIssueAssigneeName = (issue) => {
        const readPersonLike = (p) => {
          if (!p) return '';
          if (typeof p === 'string') return p.trim();
          if (typeof p === 'object') {
            const candidate =
              p.username ||
              p.displayName ||
              p.name ||
              p.login ||
              p.email ||
              p.fullName ||
              p.label ||
              p.value ||
              '';
            return String(candidate || '').trim();
          }
          return '';
        };

        const fromAssignee = readPersonLike(issue?.assignee);
        if (fromAssignee) return fromAssignee;

        const fromAssignedTo = readPersonLike(issue?.assignedTo || issue?.assigned_to);
        if (fromAssignedTo) return fromAssignedTo;

        const fromAssigneeName = readPersonLike(issue?.assigneeName || issue?.assigneeDisplayName);
        if (fromAssigneeName) return fromAssigneeName;

        const dev = issue?.developer;
        if (Array.isArray(dev) && dev.length > 0) {
          const first = readPersonLike(dev[0]);
          if (first) return first;
        } else {
          const d = readPersonLike(dev);
          if (d) return d;
        }

        return 'Unassigned';
      };
      let assignees = [];
      let sprintSource = null;
      let releaseSource = null;
      let freshSprints = [];
      let freshReleases = [];
      /** True when assignees came from sprint|release document (before user-list fallback). Bucket rows only allowed in that case. */
      let usedSprintReleaseAssigneeList = false;
      /** When true, team was built from full user list — filter to issue assignees only. When false, list comes from Sprint/Release API (include every saved assignee, e.g. manually added capacity users). */
      let filterAssigneesToActiveIssues = false;
      if (isSprintCapacity && sprintId) {
        sprintSource =
          jiraData?.sprintList?.find((s) => String(s._id) === String(sprintId)) ||
          (jiraData?.Sprint && String(jiraData.Sprint._id) === String(sprintId)
            ? jiraData.Sprint
            : null);
        if (sprintSource) {
          assignees = Array.isArray(sprintSource.assignees) ? sprintSource.assignees : [];
          startDate = sprintSource.startDate;
          endDate = sprintSource.completedDate || sprintSource.endDate;
          filterAssigneesToActiveIssues = false;
          usedSprintReleaseAssigneeList = hasValidCapacityAssigneeRows(assignees);
        }
        // Always load enriched sprint list for the selected scope (incl. active) so Save+refresh stays fresh.
        try {
          const sprintListResp = await getSprintList({ sprintId });
          freshSprints = Array.isArray(sprintListResp?.data) ? sprintListResp.data : [];
          sprintSource = freshSprints.find((s) => String(s._id) === String(sprintId)) || sprintSource;
        } catch (e) {
          console.warn('Capacity planning: could not load fresh sprint list', e);
        }
        if (sprintSource) {
          assignees = Array.isArray(sprintSource.assignees) ? sprintSource.assignees : [];
          startDate = sprintSource.startDate;
          endDate = sprintSource.completedDate || sprintSource.endDate;
          filterAssigneesToActiveIssues = false;
          usedSprintReleaseAssigneeList = hasValidCapacityAssigneeRows(assignees);
        }
        if (freshSprints.length > 0) {
          dispatch(setSprintList(freshSprints));
          if (sprintSource) {
            dispatch(setSprint(sprintSource));
          }
        }
      } else if (isReleaseCapacity && releaseId) {
        releaseSource =
          jiraData?.releasesList?.find((r) => String(r._id) === String(releaseId)) ||
          (jiraData?.Release && String(jiraData.Release._id) === String(releaseId)
            ? jiraData.Release
            : null);
        if (releaseSource) {
          assignees = Array.isArray(releaseSource.assignees) ? releaseSource.assignees : [];
          startDate = releaseSource.startDate;
          endDate = releaseSource.releaseDate;
          filterAssigneesToActiveIssues = false;
          usedSprintReleaseAssigneeList = hasValidCapacityAssigneeRows(assignees);
        }
        // Always load enriched release list for the selected scope so Save+refresh stays fresh.
        try {
          const releaseListResp = await getReleaseDetails({ releaseId });
          freshReleases = Array.isArray(releaseListResp?.data) ? releaseListResp.data : [];
          releaseSource = freshReleases.find((r) => String(r._id) === String(releaseId)) || releaseSource;
        } catch (e) {
          console.warn('Capacity planning: could not load fresh release list', e);
        }
        if (releaseSource) {
          assignees = Array.isArray(releaseSource.assignees) ? releaseSource.assignees : [];
          startDate = releaseSource.startDate;
          endDate = releaseSource.releaseDate;
          filterAssigneesToActiveIssues = false;
          usedSprintReleaseAssigneeList = hasValidCapacityAssigneeRows(assignees);
        }
        if (freshReleases.length > 0) {
          dispatch(setReleasesList(freshReleases));
          if (releaseSource) {
            dispatch(setRelease(releaseSource));
          }
        }
      }
      // Fallback: build team from user list / previously saved capacity if no assignees on sprint/release
      if (!hasValidCapacityAssigneeRows(assignees)) {
        usedSprintReleaseAssigneeList = false;
        filterAssigneesToActiveIssues = true;
        const projectId = selectedProjectId;
        const cachedUsersByProject = jiraData?.userListByProjectId?.[projectId];
        let users = Array.isArray(cachedUsersByProject) ? cachedUsersByProject : [];
        if (!users.length) {
          // Fetch user list on-demand if not already loaded
          try {
            if (!userListFetchPromiseRef.current[projectId]) {
              const value = isReleaseCapacity ? 'release' : 'sprint';
              userListFetchPromiseRef.current[projectId] = (async () => {
                const resp = await getProjectManagementData({ sections: 'userList', value });
                const fetchedUsers = resp?.data?.userList || [];
                dispatch(setUserList(fetchedUsers));
                dispatch(setUserListForProject({ projectId, users: fetchedUsers }));
                return fetchedUsers;
              })()
                .catch((e) => {
                  console.warn('getProjectManagementData userList failed:', e?.message || e);
                  return [];
                })
                .finally(() => {
                  delete userListFetchPromiseRef.current[projectId];
                });
            }
            users = await userListFetchPromiseRef.current[projectId];
          } catch (e) {
            console.warn('getProjectManagementData userList failed:', e?.message || e);
            users = [];
          }
        }
        const savedCapList = Array.isArray(jiraData?.availableHours?.assignees)
          ? jiraData.availableHours.assignees
          : [];
        const savedByName = new Map(savedCapList.map((a) => [a.assignee, a]));
        const prevAssignees = jiraData?.Sprint?.assignees || jiraData?.Release?.assignees || [];
        const prevByName = new Map(prevAssignees.map((a) => [a.assignee, a]));
        const currentRoles = rolesRef.current || [];
        assignees = users.map((u) => {
          const cap = savedByName.get(u.displayName) || {};
          const prev = prevByName.get(u.displayName) || {};
          const role = prev.role || cap.role || u.role || '';
          const roleRate = currentRoles.find((r) => r.role === role);
          const manualFromSaved =
            prev.addedManually === 'yes' ||
            prev.addedManually === true ||
            cap.addedManually === 'yes' ||
            cap.addedManually === true;
          return {
            _id: prev._id || cap._id || getUniqueId(),
            assignee: u.displayName,
            email: String(
              prev.email ||
                cap.email ||
                u.email ||
                u.mail ||
                getStoredCapacityEmail(savedEmailByName, u.displayName),
            ).trim(),
            role,
            allocationType: prev.allocationType || cap.allocationType || '',
            availableHours: Number(prev.availableHours ?? cap.availableHours ?? 0),
             allocatedStoryPoints: Number(
              prev.allocatedStoryPoints ?? cap.allocatedStoryPoints ?? cap.allocatedHours ?? 0,
            ),
            allocatedHours: Number(cap.allocatedHours || 0),
            initialPlannedAllocation:
              prev.initialPlannedAllocation !== null
              && prev.initialPlannedAllocation !== undefined
              && Number.isFinite(Number(prev.initialPlannedAllocation))
                ? Number(prev.initialPlannedAllocation)
                : cap.initialPlannedAllocation !== null
                  && cap.initialPlannedAllocation !== undefined
                  && Number.isFinite(Number(cap.initialPlannedAllocation))
                  ? Number(cap.initialPlannedAllocation)
                  : undefined,
            billingRate: roleRate ? roleRate.rate : Number(prev.billingRate || cap.billingRate || 0),
            netAvailableCapacity: Number(prev.netAvailableCapacity ?? cap.netAvailableCapacity ?? 0),
            leaves: Number(cap.leaves || 0),
            addedManually: manualFromSaved ? 'yes' : 'no',
          };
        });
        const nameSetFallback = new Set(assignees.map((a) => normalizeAssigneeName(a.assignee)));
        const manualExtraSeen = new Set();
        for (const src of [...savedCapList, ...prevAssignees]) {
          if (!src?.assignee) continue;
          const n = normalizeAssigneeName(src.assignee);
          if (!n || manualExtraSeen.has(n)) continue;
          const isManual = src.addedManually === 'yes' || src.addedManually === true;
          if (!isManual) continue;
          manualExtraSeen.add(n);
          if (nameSetFallback.has(n)) continue;
          nameSetFallback.add(n);
          const role = src.role || '';
          const roleRate = currentRoles.find((r) => r.role === role);
          assignees.push({
            _id: src._id || getUniqueId(),
            assignee: src.assignee,
            email: String(
              src.email || getStoredCapacityEmail(savedEmailByName, src.assignee),
            ).trim(),
            role,
            allocationType: src.allocationType || '',
            availableHours: Number(src.availableHours ?? 0),
            allocatedStoryPoints: Number(src.allocatedStoryPoints ?? src.allocatedHours ?? 0),
            allocatedHours: Number(src.allocatedHours || 0),
            initialPlannedAllocation:
              src.initialPlannedAllocation !== null
              && src.initialPlannedAllocation !== undefined
              && Number.isFinite(Number(src.initialPlannedAllocation))
                ? Number(src.initialPlannedAllocation)
                : undefined,
            billingRate: roleRate ? roleRate.rate : Number(src.billingRate || 0),
            netAvailableCapacity: Number(src.netAvailableCapacity ?? 0),
            leaves: Number(src.leaves || 0),
            addedManually: 'yes',
          });
        }
      }
      let activeIssues = jiraData?.jiraTableData?.issues || [];
      // GitHub Issues boards still need issue-table merge for newly assigned developers.
      const hasSavedPlanAssignees = usedSprintReleaseAssigneeList && !isGithubIssues;
      if (
        !hasSavedPlanAssignees
        && (!activeIssues || activeIssues.length === 0)
        && (isSprintCapacity || isReleaseCapacity)
      ) {
        try {
          const value = isReleaseCapacity ? 'release' : 'sprint';
          const resp = await getProjectManagementData({ sections: 'jiraData', value });
          const tablePayload = resp?.data?.jiraData ?? resp?.data;
          if (Array.isArray(tablePayload?.issues) && tablePayload.issues.length > 0) {
            dispatch(setJiraTableData(tablePayload));
            activeIssues = tablePayload.issues;
          }
        } catch (jiraDataErr) {
          console.warn('Capacity planning: could not load issue table for roster merge', jiraDataErr?.message || jiraDataErr);
        }
      }
      const activeAssignees = new Set(
        activeIssues
        //   .map((issue) => normalizeAssigneeName(issue?.assignee || issue?.developer))
         .map((issue) => normalizeAssigneeName(normalizeIssueAssigneeName(issue)))
          .filter(Boolean),
      );
      if (
        filterAssigneesToActiveIssues &&
        assignees.length > 0 &&
        activeAssignees.size > 0
      ) {
        const filteredAssignees = assignees.filter((assignee) => {
          const isManual =
            assignee.addedManually === 'yes' || assignee.addedManually === true;
          if (isManual) return true;
          return activeAssignees.has(normalizeAssigneeName(assignee?.assignee));
        });
        if (filteredAssignees.length > 0) {
          assignees = filteredAssignees;
        }
      }
      if (!usedSprintReleaseAssigneeList && Array.isArray(assignees)) {
        assignees = assignees.filter((a) => {
          const name = String(a?.assignee || '').trim();
          if (isUnassignedBucketName(name)) return true;
          return !isBucketAssigneeName(name);
        });
      }

      const sprintListForCarry = freshSprints.length > 0
        ? freshSprints
        : (jiraData?.sprintList || []);
      let derivedHoursMode = isHoursBasedProject || isAzure;
      if (releaseSource) {
        derivedHoursMode = releaseSource.hours === true || isAzure;
      } else if (sprintSource) {
        derivedHoursMode = resolveStandupHoursBasedProject(sprintSource, {
          sprintList: sprintListForCarry,
        });
      }

      const projectIdForRoster = sessionStorage.getItem('projectId');
      let rosterUsers = jiraData?.userListByProjectId?.[projectIdForRoster];
      if (!Array.isArray(rosterUsers) || rosterUsers.length === 0) {
        rosterUsers = jiraData?.userList || [];
      }
      if (
        !hasSavedPlanAssignees
        && (!Array.isArray(rosterUsers) || rosterUsers.length === 0)
        && projectIdForRoster
      ) {
        try {
          const rosterValue = isReleaseCapacity ? 'release' : 'sprint';
          const resp = await getProjectManagementData({ sections: 'userList', value: rosterValue });
          rosterUsers = resp?.data?.userList || [];
          if (rosterUsers.length) {
            dispatch(setUserList(rosterUsers));
            dispatch(setUserListForProject({ projectId: projectIdForRoster, users: rosterUsers }));
          }
        } catch (rosterErr) {
          console.warn('Capacity planning: could not load userList for roster merge', rosterErr?.message || rosterErr);
        }
      }
      assignees = mergeCapacityAssigneesWithUserList({
        assignees,
        userList: rosterUsers || [],
        existingRoles: rolesRef.current || [],
        // Saved plan (including carried selection): do not dump full project roster onto the grid.
        addMissingUserListMembers: !hasSavedPlanAssignees && !hasSavedCapacitySelectionPlan(assignees),
      });
      if (!hasSavedPlanAssignees && Array.isArray(activeIssues) && activeIssues.length > 0) {
        assignees = buildStandupCapacityAssigneesFallback({
          issues: activeIssues,
          userList: rosterUsers || [],
          existingAssignees: assignees,
          isHoursBased: derivedHoursMode,
        });
      }
      if (Array.isArray(assignees)) {
        assignees = assignees.map((assignee) => ({
          ...assignee,
          email: String(
            assignee?.email || getStoredCapacityEmail(savedEmailByName, assignee?.assignee),
          ).trim(),
        }));
      }

      // Capacity fallback: when sprint/release assignees exist but have 0 allocation,
      // derive allocated (hours|estimate) from currently loaded issues/burndown.
      const savedCapacityByNormName = new Map();
      const seedSavedCapacity = (rows) => {
        for (const r of rows || []) {
          const n = normalizeAssigneeName(r?.assignee);
          if (!n) continue;
          const prior = savedCapacityByNormName.get(n) || {};
          savedCapacityByNormName.set(n, {
            ...prior,
            ...r,
            role: prior.role || r?.role || '',
            allocationType: prior.allocationType || r?.allocationType || '',
            availableHours: Number(r?.availableHours ?? prior.availableHours ?? 0),
          });
        }
      };
      seedSavedCapacity(sprintSource?.assignees);
      seedSavedCapacity(releaseSource?.assignees);

      // For hours-based sprints/releases, prefer burndown per-developer originalEstimate if available.
      const burndownDevHours = new Map();
      const burndown = (sprintSource?.burndownData || releaseSource?.burndownData) ?? null;
      const burndownDaily = Array.isArray(burndown?.dailyData) ? burndown.dailyData : [];
      const burndownDevData =
        burndownDaily.length > 0 && Array.isArray(burndownDaily[0]?.developerData)
          ? burndownDaily[0].developerData
          : [];
      const burndownTotal = Number(burndown?.totalOriginalEstimate ?? 0);
      if (derivedHoursMode && burndownDevData.length > 0) {
        for (const d of burndownDevData) {
          const nm = String(d?.developer || '').trim();
          if (!nm) continue;
          const key = normalizeAssigneeName(nm);
          const hrs = Number(d?.originalEstimate ?? 0);
          if (!Number.isFinite(hrs) || hrs <= 0) continue;
          burndownDevHours.set(key, Number(burndownDevHours.get(key) || 0) + hrs);
        }
      }

      const issueEstimateByAssignee = buildIssueEstimateTotalsByAssignee(
        activeIssues || [],
        derivedHoursMode,
      );
      const issueTicketCountsByAssignee = buildIssueTicketCountsByAssignee(activeIssues || []);
      if (Array.isArray(assignees) && (issueEstimateByAssignee.size > 0 || issueTicketCountsByAssignee.size > 0)) {
        assignees = assignees.map((a) => {
          const k = normalizeAssigneeName(a?.assignee);
          const saved = savedCapacityByNormName.get(k) || null;
          const estimated = Number(issueEstimateByAssignee.get(a?.assignee) || issueEstimateByAssignee.get(k) || 0);
          const ticketCount = Number(
            issueTicketCountsByAssignee.get(a?.assignee)
              || issueTicketCountsByAssignee.get(k)
              || a?.tickets
              || 0,
          );
          const currentAlloc = derivedHoursMode
            ? Number(a?.allocatedHours || 0)
            : Number(a?.allocatedStoryPoints ?? a?.allocatedHours ?? 0);
          const next = { ...a, tickets: ticketCount };
          if (saved) {
            if (!next.role && saved.role) next.role = saved.role;
            if (!next.allocationType && saved.allocationType) next.allocationType = saved.allocationType;
          }
          if (currentAlloc <= 0 && estimated > 0) {
            if (derivedHoursMode) {
              next.allocatedHours = estimated;
            } else {
              next.allocatedStoryPoints = estimated;
              if (isGithubIssues) {
                next.allocatedHours = estimated;
              }
            }
          }
          if (
            (assigneeHasSyncedAllocation(next, derivedHoursMode) || assigneeHasScopeTickets(next))
            && resolveCapacityPresentInPlan(next, derivedHoursMode) === 'yes'
          ) {
            next.presentInPlan = 'yes';
          }
          next.addUserModalLocked = assigneeHasScopeTickets(next) ? 'yes' : 'no';
          return next;
        });
        const existingNames = new Set(assignees.map((a) => normalizeAssigneeName(a?.assignee)));
        const allIssueNames = new Set([
          ...issueEstimateByAssignee.keys(),
          ...issueTicketCountsByAssignee.keys(),
        ]);
        allIssueNames.forEach((assigneeName) => {
          const key = normalizeAssigneeName(assigneeName);
          if (!key || existingNames.has(key)) return;
          const estimated = Number(issueEstimateByAssignee.get(assigneeName) || 0);
          const ticketCount = Number(issueTicketCountsByAssignee.get(assigneeName) || 0);
          if (ticketCount <= 0 && estimated <= 0) return;
          const saved = savedCapacityByNormName.get(key) || {};
          const issueRow = {
            allocatedStoryPoints: derivedHoursMode ? 0 : Number(estimated || 0),
            allocatedHours: derivedHoursMode || isGithubIssues ? Number(estimated || 0) : 0,
            tickets: ticketCount,
          };
          existingNames.add(key);
          assignees.push({
            _id: getUniqueId(),
            assignee: assigneeName,
            email: getStoredCapacityEmail(savedEmailByName, assigneeName),
            role: saved.role || '',
            allocationType: saved.allocationType || '',
            availableHours: Number(saved.availableHours ?? 0),
            ...issueRow,
            billingRate: 0,
            leaves: 0,
            addedManually: 'no',
            sprintOrReleaseUser: 'yes',
            presentInPlan: 'yes',
            addUserModalLocked: ticketCount > 0 ? 'yes' : 'no',
          });
        });
      }

      // If burndown provides per-developer original estimates (hours-mode), trust it to avoid collapsing into "Unassigned".
      if (derivedHoursMode && burndownDevHours.size > 0 && Array.isArray(assignees)) {
        let sum = 0;
        for (const v of burndownDevHours.values()) sum += Number(v || 0);
        const leftover = Math.max((Number.isFinite(burndownTotal) ? burndownTotal : 0) - sum, 0);
        assignees = assignees.map((a) => {
          const nm = String(a?.assignee || '').trim();
          const key = normalizeAssigneeName(nm);
          if (!nm) return a;
          if (isUnassignedBucketName(nm)) {
            return { ...a, allocatedHours: leftover };
          }
          if (isBucketAssigneeName(nm)) return a;
          const hrs = Number(burndownDevHours.get(key) || 0);
          return { ...a, allocatedHours: hrs };
        });
      }
      if (usedSprintReleaseAssigneeList && Array.isArray(assignees)) {
        offPlanManualAssigneesRef.current = assignees.filter(
          (a) => !assigneeHasScopeTickets(a)
            && !assigneeHasSyncedAllocation(a, derivedHoursMode)
            && resolveCapacityPresentInPlan(a, derivedHoursMode) === 'no',
        );
      } else {
        offPlanManualAssigneesRef.current = [];
      }
      // Keep purged manual norms across refresh within the same sprint/release
      // (cleared only on scope change). Drop hard-deleted manuals if API/cache is stale.
      if (purgedOffPlanManualNorms.length > 0 && Array.isArray(assignees)) {
        const purgedSet = new Set(purgedOffPlanManualNorms.map((k) => normCapacityName(k)));
        assignees = assignees.filter((a) => {
          const nk = normCapacityName(a?.assignee);
          if (!nk || !purgedSet.has(nk)) return true;
          const isManual = a?.addedManually === 'yes' || a?.addedManually === true;
          return !isManual;
        });
      }
      // Always update grid, even when no assignees (shows a blank editable row)
      const hoursMode = derivedHoursMode;
      if (Array.isArray(assignees)) {
        assignees = assignees.map((a) => {
          const name = String(a?.assignee || '').trim();
          if (!isUnassignedBucketName(name)) return a;
          if (!shouldShowUnassignedCapacityApiAssignee(a, hoursMode)) return a;
          return { ...a, presentInPlan: 'yes' };
        });
      }
      const sprintListForPlanCarry = freshSprints.length > 0
        ? freshSprints
        : (jiraData?.sprintList || []);
      const releaseListForPlanCarry = freshReleases.length > 0
        ? freshReleases
        : (jiraData?.releasesList || []);
      isFutureScopeRef.current =
        String(sprintSource?.state || releaseSource?.state || '').toLowerCase() === 'future'
        || isSequentialReleaseLiveCarryTarget(releaseListForPlanCarry, releaseSource);
      // Carry from prior sprint/release first; then re-apply any just-saved grid values so
      // a future-local edit is never wiped by active→future overlay on the same fetch.
      if (Array.isArray(assignees)) {
        const sprintState = String(sprintSource?.state || '').toLowerCase();
        const isFutureSprint = sprintState === 'future';
        const isActiveSprint = sprintState === 'active' || sprintState === 'current';
        if (sprintSource && (isFutureSprint || isActiveSprint)) {
          const sourceSprint = resolvePriorSprintPlanSource(sprintListForPlanCarry, sprintSource);
          if (sourceSprint?.assignees?.length) {
            const hasSavedPlan = hasSavedCapacitySelectionPlan(assignees);
            const fromActive =
              String(sourceSprint?.state || '').toLowerCase() === 'active'
              || String(sourceSprint?.state || '').toLowerCase() === 'current';
            if (isFutureSprint && fromActive) {
              assignees = applyCarryForwardActivePlanToFuture(
                assignees,
                sourceSprint.assignees,
                derivedHoursMode,
              );
            } else if (isActiveSprint && hasSavedPlan) {
              assignees = unionMissingCarryForwardAssignees(assignees, sourceSprint.assignees);
            } else {
              assignees = applyCarryForwardCapacityPlanToAssignees(
                assignees,
                sourceSprint.assignees,
                derivedHoursMode,
              );
            }
          }
        } else if (releaseSource && isUnreleasedRelease(releaseSource)) {
          const sourceRelease = resolvePriorReleasePlanSource(releaseListForPlanCarry, releaseSource);
          if (sourceRelease?.assignees?.length) {
            const hasSavedPlan = hasSavedCapacitySelectionPlan(assignees);
            if (isLiveReleasePlanSource(sourceRelease)) {
              assignees = applyCarryForwardActivePlanToFuture(
                assignees,
                sourceRelease.assignees,
                derivedHoursMode,
              );
            } else if (hasSavedPlan) {
              assignees = unionMissingCarryForwardAssignees(assignees, sourceRelease.assignees);
            } else {
              assignees = applyCarryForwardCapacityPlanToAssignees(
                assignees,
                sourceRelease.assignees,
                derivedHoursMode,
              );
            }
          }
        }
      }
      const preservedAvailabilityRows = preserveSavedAvailabilityRef.current;
      if (preservedAvailabilityRows?.length && Array.isArray(assignees)) {
        assignees = applySavedCapacityAvailabilityToAssignees(
          assignees,
          preservedAvailabilityRows,
        );
        if (isFutureScopeRef.current) {
          const savedNorms = new Set(
            preservedAvailabilityRows
              .map((row) => normCapacityName(row?.name || row?.assignee))
              .filter(Boolean),
          );
          assignees = assignees.map((row) => {
            const norm = normCapacityName(row?.assignee);
            if (!norm || !savedNorms.has(norm)) return row;
            if (row?.modified === true) return row;
            return { ...row, modified: true };
          });
        }
        preserveSavedAvailabilityRef.current = null;
      } else if (sameScopeAsLoadedGrid && teamDataRef.current?.length && Array.isArray(assignees)) {
        assignees = mergeLocalCapacityAvailabilityOverApi(assignees, teamDataRef.current);
      }
      loadedPlanLockedByNormRef.current = new Map(
        (assignees || [])
          .map((row) => {
            const norm = normCapacityName(row?.assignee);
            if (!norm) return null;
            return [norm, row?.planLockedLocally === 'yes' ? 'yes' : 'no'];
          })
          .filter(Boolean),
      );
      loadedModifiedByNormRef.current = new Map(
        (assignees || [])
          .map((row) => {
            const norm = normCapacityName(row?.assignee);
            if (!norm) return null;
            return [norm, row?.modified === true];
          })
          .filter(Boolean),
      );
      const mockProject = {
        assignees: assignees || [],
        hours: hoursMode,
        startDate,
        endDate,
      };
      const holidays = await holidayPromise;
      updateTeamDataWithUserData(mockProject, holidays);
      loadedCapacityScopeKeyRef.current = scopeKey;
    } catch (error) {
      console.error('Error fetching project data:', error);
      setTeamData([]);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    selectedValue.value,
    isHoursBasedProject,
    isAzure,
    updateTeamDataWithUserData,
    jiraData?.selectedValue,
    jiraData?.holidayListByCompanyId,
    jiraData?.sprintList,
    jiraData?.Sprint,
    jiraData?.releasesList,
    jiraData?.Release,
    jiraData?.userListByProjectId,
    jiraData?.availableHours,
    jiraData?.jiraTableData,
    isGithubIssues,
    purgedOffPlanManualNorms,
  ]);

  fetchProjectDataRef.current = fetchProjectData;

  const mapRowsToCapacityAssignees = useCallback(
    (rows) =>
      rows.map((member) => {
        const norm = normCapacityName(member.name);
        const planLockedLocally =
          isFutureScopeRef.current && locallyEditedNormsRef.current.has(norm)
            ? 'yes'
            : loadedPlanLockedByNormRef.current.get(norm) === 'yes'
              ? 'yes'
              : 'no';
        const modified =
          isFutureScopeRef.current
          && (
            locallyEditedNormsRef.current.has(norm)
            || member.modified === true
            || loadedModifiedByNormRef.current.get(norm) === true
          );
        const initialPlanned =
          member.initialPlannedAllocation !== null
          && member.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(member.initialPlannedAllocation))
            ? Number(member.initialPlannedAllocation)
            : undefined;
        return {
          assignee: isUnassignedBucketName(member.name) ? 'Unassigned' : member.name,
          email: String(member.email || '').trim(),
          role: member.role,
          allocationType: member.allocationType || '',
          availableHours: Number(member.availableHours ?? 0),
          allocatedHours: isHoursBasedProject ? member.allocatedHours : member.allocatedStoryPoints,
          tickets: Number(member.tickets || 0),
          billingRate: member.billingRate,
          leaves: Number(member.leaves || 0),
          holiday: Number(member.holiday || 0),
          availableDays: Number(member.availableDays || 0),
          netAvailableCapacity: Number(member.netAvailableCapacity ?? 0),
          addedManually: isManuallyAddedCapacityRow(member) ? 'yes' : 'no',
          presentInPlan: resolveCapacityPresentInPlan(member, isHoursBasedProject),
          sprintOrReleaseUser:
            resolveCapacityPresentInPlan(member, isHoursBasedProject) === 'no'
              ? 'no'
              : isManuallyAddedCapacityRow(member)
                ? 'no'
                : 'yes',
          planLockedLocally,
          modified,
          addUserModalLocked: resolveAddUserModalLockedForSave(member, isHoursBasedProject),
          ...(initialPlanned !== undefined ? { initialPlannedAllocation: initialPlanned } : {}),
        };
      }),
    [isHoursBasedProject],
  );

  const patchReduxAfterCapacitySave = useCallback(
    (assigneesPayload, dataKey, mode) => {
      const modeLower = String(mode || '').toLowerCase();
      const isSprint = modeLower === 'sprint' || mode === APP_STRINGS.VALUE_SPRINT;
      if (isSprint) {
        const list = Array.isArray(jiraData?.sprintList) ? [...jiraData.sprintList] : [];
        const idx = list.findIndex((s) => String(s._id) === String(dataKey));
        if (idx >= 0) {
          list[idx] = { ...list[idx], assignees: assigneesPayload, assigneeCopiedForToday: true };
          dispatch(setSprintList(list));
        }
        if (jiraData?.Sprint && String(jiraData.Sprint._id) === String(dataKey)) {
          dispatch(setSprint({ ...jiraData.Sprint, assignees: assigneesPayload, assigneeCopiedForToday: true }));
        }
        return;
      }
      const list = Array.isArray(jiraData?.releasesList) ? [...jiraData.releasesList] : [];
      const idx = list.findIndex((r) => String(r._id) === String(dataKey));
        if (idx >= 0) {
          list[idx] = { ...list[idx], assignees: assigneesPayload, assigneeCopiedForToday: true };
          dispatch(setReleasesList(list));
        }
        if (jiraData?.Release && String(jiraData.Release._id) === String(dataKey)) {
          dispatch(setRelease({ ...jiraData.Release, assignees: assigneesPayload, assigneeCopiedForToday: true }));
        }
    },
    [dispatch, jiraData?.Sprint, jiraData?.Release, jiraData?.releasesList, jiraData?.sprintList],
  );

  const persistCapacityAssignees = useCallback(
    async (rows, removedFromPlan = []) => {
      const sprintId = sessionStorage.getItem('sprintId');
      const releaseId = sessionStorage.getItem('releaseId');
      const projectId = sessionStorage.getItem('projectId');
      const dataKey =
        selectedValue.value === APP_STRINGS.VALUE_SPRINT ? sprintId : releaseId;
      const storageMode = String(selectedValue.value || '').toLowerCase();
      if (!projectId || !dataKey) {
        throw new Error('Select a project, board, and sprint or release before saving.');
      }
      let assigneesPayload = mapRowsToCapacityAssignees(rows);
      persistCapacityEmailMap({
        projectId,
        mode: storageMode,
        dataKey,
        rows: [
          ...rows,
          ...(offPlanManualAssigneesRef.current || []).map((assignee) => ({
            name: assignee?.assignee,
            email: assignee?.email,
          })),
        ],
      });
      const payloadNorms = new Set(
        assigneesPayload.map((a) => normCapacityName(a.assignee)),
      );
      for (const ent of removedFromPlan) {
        const name = String(ent?.name || '').trim();
        if (!name) continue;
        const key = normCapacityName(name);
        if (payloadNorms.has(key)) continue;
        payloadNorms.add(key);
        const canonicalName = isUnassignedBucketName(name) ? 'Unassigned' : name;
        assigneesPayload.push({
          assignee: canonicalName,
          email: String(ent?.email || '').trim(),
          role: '',
          allocationType: '',
          availableHours: 0,
          allocatedHours: 0,
          billingRate: 0,
          holiday: 0,
          leaves: 0,
          addedManually: ent.manual ? 'yes' : 'no',
          sprintOrReleaseUser: ent.manual ? 'no' : 'yes',
          presentInPlan: 'no',
          planLockedLocally: isFutureScopeRef.current ? 'yes' : 'no',
          addUserModalLocked: 'no',
        });
      }
      const payloadHasOffPlanManual = assigneesPayload.some(
        (a) =>
          (a.addedManually === 'yes' || a.addedManually === true) && a.presentInPlan === 'no',
      );
      if (!payloadHasOffPlanManual && offPlanManualAssigneesRef.current?.length > 0) {
        const norms = new Set(assigneesPayload.map((a) => normCapacityName(a.assignee)));
        for (const a of offPlanManualAssigneesRef.current) {
          const aname = String(a.assignee || '').trim();
          if (!aname) continue;
          const k = normCapacityName(aname);
          if (norms.has(k)) continue;
          norms.add(k);
          const manual = a.addedManually === 'yes' || a.addedManually === true;
          const canonicalName = isUnassignedBucketName(aname) ? 'Unassigned' : aname;
          assigneesPayload.push({
            assignee: canonicalName,
            email: String(a.email || '').trim(),
            role: a.role || '',
            allocationType: a.allocationType || '',
            availableHours: Number(a.availableHours ?? 0),
            allocatedHours: Number(a.allocatedHours || 0),
            billingRate: Number(a.billingRate || 0),
            holiday: Number(a.holiday ?? 0),
            leaves: Number(a.leaves ?? 0),
            netAvailableCapacity: Number(a.netAvailableCapacity ?? 0),
            addedManually: manual ? 'yes' : 'no',
            sprintOrReleaseUser: a.sprintOrReleaseUser === 'yes' ? 'yes' : 'no',
            presentInPlan: 'no',
            planLockedLocally: isFutureScopeRef.current ? 'yes' : 'no',
            addUserModalLocked: 'no',
          });
        }
      }
      const dataToSave = {
        projectKey: projectId,
        [selectedValue.value.toLowerCase() + 'Id']: dataKey,
        assignees: assigneesPayload,
      };
      await addCapacity([dataToSave]);
      patchReduxAfterCapacitySave(assigneesPayload, dataKey, selectedValue.value);
      for (const row of assigneesPayload) {
        const norm = normCapacityName(row?.assignee);
        if (!norm) continue;
        loadedPlanLockedByNormRef.current.set(norm, row.planLockedLocally === 'yes' ? 'yes' : 'no');
        loadedModifiedByNormRef.current.set(norm, row.modified === true);
      }
      const onPlanRows = rows.filter((row) => isAssigneeOnCapacityPlan(row, isHoursBasedProject));
      const offPlanRows = rows.filter((row) => !isAssigneeOnCapacityPlan(row, isHoursBasedProject));
      offPlanManualAssigneesRef.current = offPlanRows.map((row) => ({
        _id: row.id,
        assignee: row.name,
        email: String(row.email || '').trim(),
        role: row.role || '',
        allocationType: row.allocationType || '',
        availableHours: Number(row.availableHours ?? 0),
        allocatedHours: Number(row.allocatedHours || 0),
        billingRate: Number(row.billingRate || 0),
        holiday: Number(row.holiday ?? 0),
        leaves: Number(row.leaves ?? 0),
        netAvailableCapacity: Number(row.netAvailableCapacity ?? 0),
        addedManually: isManuallyAddedCapacityRow(row) ? 'yes' : 'no',
        sprintOrReleaseUser: row.sprintOrReleaseUser === 'yes' ? 'yes' : 'no',
        presentInPlan: 'no',
        addUserModalLocked: 'no',
      }));
      preserveSavedAvailabilityRef.current = onPlanRows;
      setTeamData(onPlanRows.map((row) => calculateDependentValues(row, isHoursBasedProject)));
    },
    [
      patchReduxAfterCapacitySave,
      mapRowsToCapacityAssignees,
      selectedValue.value,
      calculateDependentValues,
      isHoursBasedProject,
    ],
  );

  const bumpEmailValidation = useCallback(() => {
    setEmailValidationRevision((n) => n + 1);
  }, []);

  const emailHasError = useCallback(
    (rowId) => invalidEmailRowIdsRef.current.has(rowId),
    [emailValidationRevision],
  );

  const saveCapacityData = async () => {
    if (gridApiRef.current) gridApiRef.current.stopEditing();

    const rowsToSave = teamDataRef.current?.length ? teamDataRef.current : teamData;
    const nextInvalid = new Set();
    for (const row of rowsToSave) {
      const email = String(row?.email ?? '').trim();
      if (email && !isValidCapacityEmail(email)) {
        nextInvalid.add(row.id);
      }
    }
    if (nextInvalid.size > 0) {
      invalidEmailRowIdsRef.current = nextInvalid;
      bumpEmailValidation();
      gridApiRef.current?.refreshCells({ columns: ['email'], force: true });
      return;
    }
    invalidEmailRowIdsRef.current = new Set();
    bumpEmailValidation();

    try {
      setLoading(true);
      await Promise.all([
        addStoryPoints(storyPointToHourRatio),
        persistCapacityAssignees(rowsToSave),
      ]);
      setSaveStatus(true);
      setIsModalOpen(true);
      setTimeout(() => setSaveStatus(false), 500);
    } catch (error) {
      console.error('Error saving capacity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = useCallback(
    (uniqueKey, field, value) => {
      if (field === 'email' && invalidEmailRowIdsRef.current.has(uniqueKey)) {
        const trimmed = String(value ?? '').trim();
        if (!trimmed || isValidCapacityEmail(trimmed)) {
          const next = new Set(invalidEmailRowIdsRef.current);
          next.delete(uniqueKey);
          invalidEmailRowIdsRef.current = next;
          bumpEmailValidation();
        }
      }
      setTeamData((prevTeamData) => {
        const next = prevTeamData.map((row) => {
          if (row.id === uniqueKey) {
            if (isFutureScopeRef.current && row.name?.trim()) {
              locallyEditedNormsRef.current.add(normCapacityName(row.name));
            }
            const updatedRow = { ...row };
            if (field === 'role') {
              const selectedRole = roles.find((role) => role.role === value);
              updatedRow.role = value;
              updatedRow.billingRate = selectedRole ? selectedRole.rate : 0;
            } else if (field === 'allocationType') {
              updatedRow.allocationType = value;
            } else {
              updatedRow[field] = value;
            }
            return calculateDependentValues(updatedRow, isHoursBasedProject);
          }
          return row;
        });
        const visible = next.filter((row) => shouldShowUnassignedCapacityGridRow(row, isHoursBasedProject));
        teamDataRef.current = visible;
        return visible;
      });
    },
    [roles, storyPointToHourRatio, calculateDependentValues, isHoursBasedProject, bumpEmailValidation],
  );

  const openAddUsersModal = useCallback(async () => {
    const rawMode =
      selectedValue?.value || jiraData?.selectedValue || sessionStorage.getItem('typeValue') || '';
    const mode = String(rawMode).toLowerCase();
    const isSprintCapacity = mode === 'sprint';
    const isReleaseCapacity = mode === 'release';
    const projectId = sessionStorage.getItem('projectId') || '';
    const dataKeyForEmails = isSprintCapacity
      ? sessionStorage.getItem('sprintId') || ''
      : isReleaseCapacity
        ? sessionStorage.getItem('releaseId') || ''
        : '';
    const savedEmailByName = readCapacityEmailMap({
      projectId,
      mode,
      dataKey: dataKeyForEmails,
    });
    let assignees = [];
    let currentScopeDoc = null;
    let sprintListForCarry = jiraData?.sprintList || [];
    let releaseListForCarry = jiraData?.releasesList || [];
    try {
      if (isSprintCapacity) {
        const sprintId = sessionStorage.getItem('sprintId');
        if (sprintId) {
          try {
            const resp = await getSprintList({ sprintId });
            const list = Array.isArray(resp?.data) ? resp.data : [];
            sprintListForCarry = list.length > 0 ? list : sprintListForCarry;
            const doc = list.find((s) => String(s._id) === String(sprintId));
            assignees = Array.isArray(doc?.assignees) ? doc.assignees : [];
            currentScopeDoc = doc
              || jiraData?.sprintList?.find((s) => String(s._id) === String(sprintId))
              || (jiraData?.Sprint && String(jiraData.Sprint._id) === String(sprintId)
                ? jiraData.Sprint
                : null);
            if (list.length > 0) {
              dispatch(setSprintList(list));
              if (doc) dispatch(setSprint(doc));
            }
          } catch (e) {
            console.warn('Add users modal: could not load sprint assignees', e);
          }
        }
      } else if (isReleaseCapacity) {
        const releaseId = sessionStorage.getItem('releaseId');
        if (releaseId) {
          try {
            const resp = await getReleaseDetails({ releaseId });
            const list = Array.isArray(resp?.data) ? resp.data : [];
            releaseListForCarry = list.length > 0 ? list : releaseListForCarry;
            const doc = list.find((r) => String(r._id) === String(releaseId));
            assignees = Array.isArray(doc?.assignees) ? doc.assignees : [];
            currentScopeDoc = doc
              || jiraData?.releasesList?.find((r) => String(r._id) === String(releaseId))
              || (jiraData?.Release && String(jiraData.Release._id) === String(releaseId)
                ? jiraData.Release
                : null);
            if (list.length > 0) {
              dispatch(setReleasesList(list));
              if (doc) dispatch(setRelease(doc));
            }
          } catch (e) {
            console.warn('Add users modal: could not load release assignees', e);
          }
        }
      }
    } catch (e) {
      console.warn('Add users modal: could not load sprint/release assignees', e);
      assignees = [];
    }

    if (Array.isArray(assignees) && assignees.length > 0) {
      if (isSprintCapacity && currentScopeDoc) {
        const sourceSprint = resolvePriorSprintPlanSource(sprintListForCarry, currentScopeDoc);
        if (sourceSprint?.assignees?.length) {
          const state = String(currentScopeDoc?.state || '').toLowerCase();
          const hasSavedPlan = hasSavedCapacitySelectionPlan(assignees);
          const fromActive =
            String(sourceSprint?.state || '').toLowerCase() === 'active'
            || String(sourceSprint?.state || '').toLowerCase() === 'current';
          if (state === 'future' && fromActive) {
            assignees = applyCarryForwardActivePlanToFuture(
              assignees,
              sourceSprint.assignees,
              isHoursBasedProject,
            );
          } else if ((state === 'active' || state === 'current') && hasSavedPlan) {
            assignees = unionMissingCarryForwardAssignees(assignees, sourceSprint.assignees);
          } else {
            assignees = applyCarryForwardCapacityPlanToAssignees(
              assignees,
              sourceSprint.assignees,
              isHoursBasedProject,
            );
          }
        }
      } else if (isReleaseCapacity && currentScopeDoc) {
        const sourceRelease = resolvePriorReleasePlanSource(releaseListForCarry, currentScopeDoc);
        if (sourceRelease?.assignees?.length) {
          const hasSavedPlan = hasSavedCapacitySelectionPlan(assignees);
          if (isLiveReleasePlanSource(sourceRelease) && isUnreleasedRelease(currentScopeDoc)) {
            assignees = applyCarryForwardActivePlanToFuture(
              assignees,
              sourceRelease.assignees,
              isHoursBasedProject,
            );
          } else if (isUnreleasedRelease(currentScopeDoc) && hasSavedPlan) {
            assignees = unionMissingCarryForwardAssignees(assignees, sourceRelease.assignees);
          } else {
            assignees = applyCarryForwardCapacityPlanToAssignees(
              assignees,
              sourceRelease.assignees,
              isHoursBasedProject,
            );
          }
        }
      }
    }

    const merged = assignees.map((assignee) => ({
      ...assignee,
      email: String(
        assignee?.email || getStoredCapacityEmail(savedEmailByName, assignee?.assignee),
      ).trim(),
    }));
    const purgedSet = new Set((purgedOffPlanManualNorms || []).map((k) => normCapacityName(k)));
    const filterPurgedManual = (list) =>
      (list || []).filter((a) => {
        const nk = normCapacityName(a?.assignee);
        if (!nk || !purgedSet.has(nk)) return true;
        const isManual = a?.addedManually === 'yes' || a?.addedManually === true;
        return !isManual;
      });
    let rosterMerged = filterPurgedManual(merged);
    const seen = new Set();
    for (const a of rosterMerged) {
      const nm = String(a?.assignee || '').trim();
      if (nm) seen.add(normCapacityName(nm));
    }
    if (projectId) {
      let projectUsers = [];
      const fromStore = Array.isArray(jiraData?.projectList)
        ? jiraData.projectList.find((p) => String(p._id) === String(projectId))
        : null;
      if (fromStore && Array.isArray(fromStore.projectUsers)) {
        projectUsers = fromStore.projectUsers;
      }
      if (!projectUsers.length) {
        try {
          const projectsResponse = await getProjectList();
          const list = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
          const fresh = list.find((p) => String(p._id) === String(projectId));
          if (fresh && Array.isArray(fresh.projectUsers) && fresh.projectUsers.length) {
            projectUsers = fresh.projectUsers;
            dispatch(setProjectList(list));
          }
        } catch (e) {
          console.warn('Add users modal: could not load project roster (projectUsers)', e);
        }
      }
      for (const u of projectUsers) {
        if (u && u.active === false) continue;
        const dn = String(u?.displayName || '').trim();
        const dnNorm = normCapacityName(dn);
        if (!dn || !dnNorm || dnNorm === 'unassigned' || dnNorm === 'none' || dnNorm === 'n/a') {
          continue;
        }
        const existingOffPlan = assignees.find(
          (a) =>
            normCapacityName(a?.assignee) === dnNorm
            && (a.presentInPlan === 'no' || a.presentInPlan === false),
        );
        if (existingOffPlan && !purgedSet.has(dnNorm)) continue;
        const nk = dnNorm;
        if (seen.has(nk)) continue;
        seen.add(nk);
        rosterMerged.push({
          assignee: dn,
          email: String(
            u?.email || u?.mail || getStoredCapacityEmail(savedEmailByName, dn),
          ).trim(),
          role: u.role || '',
          allocationType: '',
          availableHours: 0,
          allocatedHours: 0,
          billingRate: 0,
          leaves: 0,
          addedManually: 'no',
          sprintOrReleaseUser: 'no',
        });
      }
    }

    setAddModalSprintReleaseAssignees(rosterMerged);
    setIsAddUsersModalOpen(true);
  }, [
    dispatch,
    jiraData?.projectList,
    jiraData?.selectedValue,
    jiraData?.sprintList,
    jiraData?.Sprint,
    jiraData?.releasesList,
    jiraData?.Release,
    selectedValue?.value,
    purgedOffPlanManualNorms,
  ]);

  const applyAddUsersModal = useCallback(
    async (newTeamData, prevTeamDataSnapshot) => {
      const prev =
        prevTeamDataSnapshot !== undefined && prevTeamDataSnapshot !== null
          ? prevTeamDataSnapshot
          : teamDataRef.current;
      const newNorms = new Set(
        newTeamData.filter((r) => r.name?.trim()).map((r) => normCapacityName(r.name)),
      );
      const removedEntries = [];
      for (const r of prev) {
        if (!r.name?.trim()) continue;
        const k = normCapacityName(r.name);
        if (newNorms.has(k)) continue;
        /** Roster-locked Jira rows stay on plan; manually added rows can always be taken off and recalled in the modal. */
        if (isSprintOrReleaseLockedRow(r) && !isManuallyAddedCapacityRow(r)) continue;
        removedEntries.push({
          name: r.name.trim(),
          email: String(r.email || '').trim(),
          manual: isManuallyAddedCapacityRow(r),
        });
      }

      setLoading(true);
      try {
        if (isFutureScopeRef.current) {
          const prevNorms = new Set(
            (prev || []).filter((r) => r.name?.trim()).map((r) => normCapacityName(r.name)),
          );
          for (const r of newTeamData) {
            const k = normCapacityName(r?.name);
            if (k && !prevNorms.has(k)) locallyEditedNormsRef.current.add(k);
          }
          for (const ent of removedEntries) {
            const k = normCapacityName(ent?.name);
            if (k) locallyEditedNormsRef.current.add(k);
          }
        }
        await persistCapacityAssignees(newTeamData, removedEntries);
        const onPlanNorms = new Set(
          newTeamData
            .filter((r) => r.name?.trim())
            .map((r) => normCapacityName(r.name)),
        );
        if (onPlanNorms.size > 0) {
          setPurgedOffPlanManualNorms((prev) =>
            (prev || []).filter((k) => !onPlanNorms.has(normCapacityName(k))),
          );
        }
        const offPlanManualNorms = new Set(
          newTeamData
            .filter((r) => r.presentInPlan === 'no' || r.presentInPlan === false)
            .map((r) => normCapacityName(r.name)),
        );
        setModalRecalledOffPlan((recall) => {
          const map = new Map(recall.map((e) => [normCapacityName(e.name), e]));
          for (const ent of removedEntries) {
            if (offPlanManualNorms.has(normCapacityName(ent.name))) continue;
            map.set(normCapacityName(ent.name), ent);
          }
          for (const k of [...map.keys()]) {
            if (newNorms.has(k) || offPlanManualNorms.has(k)) {
              map.delete(k);
            }
          }
          return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
        });
      } catch (error) {
        console.error('Error saving capacity from Add Users modal:', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [persistCapacityAssignees],
  );

  const requestRemoveManualUserFromPlan = useCallback((rowId) => {
    setManualDeleteConfirmRowId(rowId);
  }, []);

  const confirmRemoveManualUserFromPlan = useCallback(async () => {
    const rawId = manualDeleteConfirmRowId;
    if (rawId == null) return;

    const idStr = String(rawId);
    setManualDeleteConfirmRowId(null);

    const dropPurgedFromModalAssignees = (nk) => {
      if (!nk) return;
      setAddModalSprintReleaseAssignees((prev) =>
        (prev || []).filter((a) => {
          if (normCapacityName(a?.assignee) !== nk) return true;
          const isManual = a?.addedManually === 'yes' || a?.addedManually === true;
          return !isManual;
        }),
      );
    };

    if (idStr.startsWith('recall:')) {
      const nk = idStr.slice(7);
      setModalRecalledOffPlan((prev) =>
        prev.filter((e) => !(e.manual && normCapacityName(e.name) === nk)),
      );
      return;
    }

    if (idStr.startsWith('offplan:')) {
      const nk = idStr.slice(8);
      setPurgedOffPlanManualNorms((prev) => [...new Set([...prev, nk])]);
      offPlanManualAssigneesRef.current = offPlanManualAssigneesRef.current.filter(
        (a) => normCapacityName(a.assignee) !== nk,
      );
      setModalRecalledOffPlan((prev) =>
        prev.filter((e) => !(e.manual && normCapacityName(e.name) === nk)),
      );
      dropPurgedFromModalAssignees(nk);
      return;
    }

    const row = teamDataRef.current.find((r) => String(r.id) === idStr);
    let nk = row?.name ? normCapacityName(row.name) : null;
    if (!nk) {
      const fromOff = offPlanManualAssigneesRef.current.find((a) => String(a._id || '') === idStr);
      if (fromOff?.assignee) nk = normCapacityName(fromOff.assignee);
    }
    if (!nk) {
      const fromModal = (addModalSprintReleaseAssignees || []).find(
        (a) => String(a._id || '') === idStr,
      );
      if (fromModal?.assignee) nk = normCapacityName(fromModal.assignee);
    }
    const prevOffPlanSnapshot = [...offPlanManualAssigneesRef.current];
    if (nk) {
      offPlanManualAssigneesRef.current = offPlanManualAssigneesRef.current.filter(
        (a) => normCapacityName(a.assignee) !== nk,
      );
    } else {
      offPlanManualAssigneesRef.current = offPlanManualAssigneesRef.current.filter(
        (a) => String(a._id || '') !== idStr,
      );
    }

    const prev = teamDataRef.current;
    const next = prev.filter((r) => String(r.id) !== idStr);

    try {
      setLoading(true);
      await persistCapacityAssignees(next);
      if (nk) {
        setPurgedOffPlanManualNorms((p) => [...new Set([...p, nk])]);
        setModalRecalledOffPlan((prevRecall) =>
          prevRecall.filter((e) => !(e.manual && normCapacityName(e.name) === nk)),
        );
        dropPurgedFromModalAssignees(nk);
      }
      setTeamData(next);
      teamDataRef.current = next;
    } catch (error) {
      offPlanManualAssigneesRef.current = prevOffPlanSnapshot;
      console.error('Error persisting capacity after removing user:', error);
    } finally {
      setLoading(false);
    }
  }, [manualDeleteConfirmRowId, persistCapacityAssignees, addModalSprintReleaseAssignees]);

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    if (params.api) params.api.sizeColumnsToFit();
  }, []);

  const isEditingAllowed = useMemo(() => {
    if (selectedValue.value === APP_STRINGS.VALUE_SPRINT) {
      const currentSprint = getAllSprintList.find((sprint) => sprint._id === selectedSprint.id);
      const state = currentSprint?.state?.toLowerCase();
      return state === 'active' || state === 'current' || state === 'future';
    } else if (selectedValue.value === APP_STRINGS.VALUE_RELEASE) {
      const currentRelease = getAllReleaseList.find(
        (release) => release._id === selectedRelease.id,
      );
      const status = currentRelease?.status?.toLowerCase();
      return status === 'unreleased' || status === 'active';
    }
    return false;
  }, [
    selectedValue.value,
    getAllSprintList,
    selectedSprint.id,
    getAllReleaseList,
    selectedRelease.id,
  ]);

  const hasInitialPlannedData = useMemo(
    () =>
      (Array.isArray(teamData) ? teamData : []).some(
        (row) =>
          row?.initialPlannedAllocation !== null
          && row?.initialPlannedAllocation !== undefined
          && Number.isFinite(Number(row.initialPlannedAllocation)),
      ),
    [teamData],
  );

  const enhancedColumns = useMemo(
    () =>
      allColumns.map((col) => {
        const updatedCol = { ...col };

        if (updatedCol.field === 'initialPlannedAllocation') {
          // Feature is new: hide empty column on historical scopes until sync populates data
          updatedCol.hide = !hasInitialPlannedData;
        }

        if (updatedCol.field === 'billingRate' || updatedCol.field === 'totalBilling') {
          updatedCol.hide = !canViewBillingRate;
        }

        if (!isEditingAllowed) {
          updatedCol.editable = false;
          if (updatedCol.cellRenderer) {
            updatedCol.cellRendererParams = {
              ...(updatedCol.cellRendererParams || {}),
              calculateDependentValues,
              roles,
              handleInputChange,
              emailHasError,
              disabled: true,
            };
          }
        } else {
          if (updatedCol.cellRenderer) {
            updatedCol.cellRendererParams = {
              ...(updatedCol.cellRendererParams || {}),
              calculateDependentValues,
              roles,
              handleInputChange,
              emailHasError,
              disabled: false,
            };
          }
        }

        return updatedCol;
      }),
    [
      allColumns,
      calculateDependentValues,
      roles,
      handleInputChange,
      emailHasError,
      isEditingAllowed,
      hasInitialPlannedData,
      canViewBillingRate,
    ],
  );

  const capacityPlanningGridOptions = useMemo(
    () => ({
      rowHeight: CAPACITY_PLANNING_ROW_HEIGHT,
    }),
    [],
  );

  const memoizedTeamData = useMemo(() => teamData, [teamData]);

  useEffect(() => {
    if (jiraData) {
      if (jiraData.projectList) {
        setGetAllProjectList(jiraData.projectList || []);
      }
      setSelectedSprint({
        id: jiraData.selectedSprintId || '',
        name: jiraData.selectedSprintName || '',
      });
      setSelectedRelease({
        id: jiraData.selectedReleaseId || '',
        releaseName: jiraData.selectedReleaseName || '',
      });
      setSelectedProject({
        id: jiraData.selectedProjectId || '',
        name: jiraData.selectedProjectName || '',
      });
      const restoredBoard = restoreBoardFromSession();
      if (restoredBoard) {
        const boardsForProject = jiraData?.boardListByProjectId?.[jiraData?.selectedProjectId] || [];
        const restoredId = restoredBoard?.id;
        if (boardsForProject.length === 0) {
          setSelectedBoard(restoredBoard);
          pendingBoardValidationRef.current = restoredId || null;
        } else {
          const isValidBoard = boardsForProject.some(
            (board) => (board.id || board._id) === restoredId,
          );
          if (isValidBoard) {
            setSelectedBoard(restoredBoard);
          }
        }
      }
      setSelectedOrg({
        id: jiraData.selectedOrgId || '',
        name: jiraData.selectedOrgName || '',
      });
      setIsProjectOpen(jiraData.isProjectOpen || false);
      setIsOrganizationOpen(jiraData.isOrganizationOpen || false);
      setIsSprintOpen(jiraData.isSprintOpen || false);
      setGetAllSprintList(jiraData.sprintList || []);
      setIsValueOpen(jiraData.isValueOpen || false);
      setSelectedValue({
        label: jiraData.selectedValueLabel || APP_STRINGS.SELECT_AN_OPTION,
        value: jiraData.selectedValue || '',
      });
      setGetAllReleaseList(jiraData.releasesList || []);
    }
  }, [jiraData]);

  useEffect(() => {
    const pendingId = pendingBoardValidationRef.current;
    if (!pendingId) return;
    const boardsForProject = jiraData?.boardListByProjectId?.[jiraData?.selectedProjectId] || [];
    if (boardsForProject.length === 0) return;
    const isValidBoard = boardsForProject.some(
      (board) => (board.id || board._id) === pendingId,
    );
    if (!isValidBoard) {
      setSelectedBoard({ id: '', name: '', type: '' });
      clearBoardFromSession();
    }
    pendingBoardValidationRef.current = null;
  }, [jiraData?.boardListByProjectId, jiraData?.selectedProjectId]);

  useEffect(() => {
    if (orgsData) {
      setGetAllOrgsList(orgsData);
    }
  }, [orgsData]);

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const companyId = sessionStorage.getItem('companyId');
        let projects = jiraData?.projectList;
        if (!projects || projects.length === 0) {
          const projectsResponse = await getProjectList(companyId);
          projects = projectsResponse?.data || [];
          dispatch(setProjectList(projects));
        }
        if (isMounted) setGetAllProjectList(projects || []);

        let roleRates = Array.isArray(jiraData?.roleRates) ? jiraData.roleRates : null;
        let storyPointRatio = typeof jiraData?.storyPointRatio === 'number'
          ? jiraData.storyPointRatio
          : null;
        if (!roleRates || roleRates.length === 0 || storyPointRatio === null) {
          if (!roleRatesFetchPromiseRef.current) {
            roleRatesFetchPromiseRef.current = (async () => {
              const rolesData = await getRoleRatesAndStoryPoints(companyId);
              const fetchedRoleRates = rolesData?.roleRates || [];
              const fetchedStoryPointRatio =
                typeof rolesData?.storyPoints === 'number' ? rolesData.storyPoints : 8;
              dispatch(setRoleRates(fetchedRoleRates));
              dispatch(setStoryPointRatio(fetchedStoryPointRatio));
              return { roleRates: fetchedRoleRates, storyPointRatio: fetchedStoryPointRatio };
            })()
              .catch((rolesError) => {
                console.warn('Could not load role rates:', rolesError);
                return { roleRates: [], storyPointRatio: 8 };
              })
              .finally(() => {
                roleRatesFetchPromiseRef.current = null;
              });
          }
          const fetched = await roleRatesFetchPromiseRef.current;
          roleRates = fetched?.roleRates || [];
          storyPointRatio = typeof fetched?.storyPointRatio === 'number'
            ? fetched.storyPointRatio
            : 8;
        }
        if (isMounted) {
          if (roleRates) setRoles(roleRates);
          if (typeof storyPointRatio === 'number') setStoryPointToHourRatio(storyPointRatio);
        }

        const projectId = getId().projectId;
        if (projectId) {
          const selectedProject = projects.find((p) => p._id === projectId);
          if (selectedProject && isMounted) {
            setSelectedProject({ id: selectedProject._id, name: selectedProject.name });
            await fetchProjectDataRef.current?.();
          }
        }
      } catch (error) {
        if (isMounted) console.error('Error fetching data:', error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchData();
    return () => {
      isMounted = false;
    };
  }, [dispatch, jiraData?.projectList, jiraData?.roleRates, jiraData?.storyPointRatio]);

  useEffect(() => {
    const issueCount = jiraData?.jiraTableData?.issues?.length ?? 0;
    if (!selectedProject?.id) {
      return;
    }
    if (capacityIssueCountRef.current === null) {
      capacityIssueCountRef.current = issueCount;
      return;
    }
    if (issueCount > 0 && capacityIssueCountRef.current !== issueCount) {
      capacityIssueCountRef.current = issueCount;
      fetchProjectDataRef.current?.();
    }
  }, [jiraData?.jiraTableData?.issues, selectedProject?.id]);

  useEffect(() => {
    const selectedSprint = jiraData?.sprintList?.find(
      (sprint) => sprint._id === jiraData?.selectedSprintId,
    );
    const selectedRelease = jiraData?.releasesList?.find(
      (release) => release._id === jiraData?.selectedReleaseId,
    );
    const isHoursFromSprint = selectedSprint?.hours === true;
    const isHoursFromRelease = selectedRelease?.hours === true;
    const isHoursBased = isHoursFromSprint || isHoursFromRelease;
    setIsHoursBasedProject(isHoursBased);
    sessionStorage.setItem('isHoursBasedProject', isHoursBased.toString());
  }, [jiraData, setIsHoursBasedProject]);

  useEffect(() => {
    if (!selectedProject?.id) {
      return;
    }
    if (typeof jiraData?.storyPointRatio === 'number') {
      setStoryPointToHourRatio(jiraData.storyPointRatio);
    }
  }, [selectedProject?.id, jiraData?.storyPointRatio]);

  useEffect(() => {
    if (teamData.length === 0) return;
    const updated = teamData.map((row) => calculateDependentValues(row, isHoursBasedProject));
    const isDifferent = JSON.stringify(updated) !== JSON.stringify(teamData);
    if (isDifferent) {
      setTeamData(updated);
    }
  }, [calculateDependentValues, isHoursBasedProject]);

useEffect(() => {
    const handleResize = () => {
      const headerHeight = 220;
      const availableHeight = window.innerHeight - headerHeight;
      const rowsThatFit = Math.floor(availableHeight / CAPACITY_PLANNING_ROW_HEIGHT);

      if (rowsThatFit <= 10) setPageSize(7);
      else if (rowsThatFit <= 11) setPageSize(8);
      else if (rowsThatFit <= 12) setPageSize(9);
      else if (rowsThatFit <= 13) setPageSize(10);
      else if (rowsThatFit <= 14) setPageSize(11);
      else if (rowsThatFit <= 15) setPageSize(12);
      else if (rowsThatFit <= 16) setPageSize(13);
      else if (rowsThatFit <= 17) setPageSize(14);
      else if (rowsThatFit <= 20) setPageSize(15);
      else setPageSize(20);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const headerHeight = 220;
      const availableHeight = window.innerHeight - headerHeight;
      setGridHeight(`${availableHeight - 50}px`);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (getAllProjectList.length === 0) return;
    const cachedBoardLists = jiraData?.boardListByProjectId || {};
    const boardCounts = {};
    for (const project of getAllProjectList) {
      const cachedBoards = cachedBoardLists[project._id];
      boardCounts[project._id] = Array.isArray(cachedBoards) ? cachedBoards.length : 0;
    }
    setProjectBoardCount(boardCounts);
  }, [getAllProjectList, jiraData?.boardListByProjectId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isBoardOpen && !event.target.closest('.board-submenu')) {
        setIsBoardOpen(false);
        setSubMenuBoards([]);
        setCurrentProjectForBoard('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBoardOpen]);

  const selectedProjectDisplayName = computeProjectDisplayName(selectedProject, selectedBoard);

  return (
    <CommonLayout>
      <>
        {loading && (
          <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center bg-light-100 bg-opacity-50 dark:bg-secondary-500 dark:bg-opacity-50 text-black dark:text-custom-gray z-50">
            <Spinner />
          </div>
        )}
        <div className="mt-20 pr-6 pl-2">
          <button
            onClick={() => navigate('/standUp')}
            className="flex items-center dark:text-[#FFFFFF] text-[#202020] hover:text-[#066FD1] font-medium mb-4 ml-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M7.707 14.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L4.414 9H17a1 1 0 110 2H4.414l3.293 3.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            Back to StandUp Page
          </button>

          <div className="mt-4 grid grid-cols-1">
            <div className="flex flex-wrap gap-2 ml-3">
              {getAllOrgsList.length > 0 && (
                <div>
                  <DropdownButton
                    buttonLabel="Select"
                    options={getAllOrgsList.map((org) => ({
                      value: org._id,
                      label: org.companyName,
                    }))}
                    onSelect={handleOrganizationChange}
                    placeholder="Select"
                    selectedOption={selectedOrg.name}
                    isOpen={isOrganizationOpen}
                    setIsOpen={setIsOrganizationOpen}
                    reference={organizationRef}
                    type="organization"
                    width="lg"
                  />
                </div>
              )}

              <div>
                <DropdownButton
                  buttonLabel="Select Project"
                  options={getAllProjectList
                    ?.filter((project) => project.isSelected && project.hideStatus === false)
                    .map((project) => ({
                      value: project._id,
                      label: project.name,
                      boardCount: projectBoardCount[project._id] || 0,
                      hasMultipleBoards: (projectBoardCount[project._id] || 0) > 1,
                    }))}
                  onSelect={handleProjectChange}
                  onOptionHover={handleProjectHover}
                  onOptionMouseLeave={handleProjectMouseLeave}
                  placeholder="Select Project"
                  selectedOption={selectedProjectDisplayName}
                  isOpen={isProjectOpen}
                  setIsOpen={setIsProjectOpen}
                  reference={projectRef}
                  type="project"
                  width="lg"
                />
              </div>
              
              {/* Board Sub-menu Overlay */}
              {isBoardOpen && subMenuBoards.length > 0 && (
                <div 
                  className="board-submenu fixed z-[9999] bg-white dark:bg-[#182433] rounded-lg shadow-lg border border-gray-200 dark:border-[#30445A] min-w-[200px]"
                  style={{
                    top: `${subMenuPosition.top}px`,
                    left: `${subMenuPosition.left}px`
                  }}
                  onMouseEnter={() => {
                  }}
                  onMouseLeave={() => {
                    setTimeout(() => {
                      const projectElement = document.querySelector(`[data-project-id="${currentProjectForBoard}"]`);
                      if (!projectElement || !projectElement.matches(':hover')) {
                        setIsBoardOpen(false);
                        setSubMenuBoards([]);
                        setCurrentProjectForBoard('');
                      }
                    }, 100);
                  }}
                >
                  <div className="py-2">
                    {subMenuBoards.map((board, index) => (
                      <div
                        key={board.id || board._id || index}
                        className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#1E2B3A] transition-colors"
                        onClick={() => {
                          handleBoardChange(board.id || board._id, currentProjectForBoard);
                        }}
                      >
                        <span className="text-sm text-gray-700 dark:text-[#D9E4F1]">
                          {board.name || board.boardName} ({board.type || board.boardType})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div>
              <DropdownButton
                  options={[
                    { value: APP_STRINGS.VALUE_SPRINT, label: sprintLabel },
                    { value: APP_STRINGS.VALUE_DATE_RANGE, label: APP_STRINGS.VALUE_DATE_RANGE },
                    ...(!isClickupList
                      ? [{ value: APP_STRINGS.VALUE_RELEASE, label: releaseLabel }]
                      : []),
                  ]}
                  onSelect={handleValueChange}
                  selectedOption={
                        selectedValue?.value === APP_STRINGS.VALUE_SPRINT
                          ? sprintLabel
                          : selectedValue?.value === APP_STRINGS.VALUE_RELEASE
                          ? releaseLabel
                          : selectedValue?.value
                      }
                  isOpen={isValueOpen}
                  setIsOpen={setIsValueOpen}
                  reference={valueRef}
                  width="lg"
                />
              </div>

              {selectedValue.value === APP_STRINGS.VALUE_SPRINT && (
                <div className="min-w-[180px]">
                  <DropdownButton
                    buttonLabel={`${APP_STRINGS.LABEL_SELECT_PREFIX}${sprintLabel}`}
                    options={capacitySprintDropdownList.map((sprint) => ({
                      value: sprint._id,
                      label: sprint.name,
                      state: sprint.state,
                      startDate: sprint.startDate,
                      endDate: sprint.endDate,
                      completeDate: sprint.completeDate,
                    }))}
                    onSelect={handleSprintChange}
                    placeholder={APP_STRINGS.SELECT_AN_OPTION}
                    selectedOption={selectedSprint?.name}
                    isOpen={isSprintOpen}
                    setIsOpen={setIsSprintOpen}
                    reference={sprintRef}
                    type={APP_STRINGS.API_SPRINT}
                    includeFutureSprints
                    width="lg"
                  />
                </div>
              )}

              {selectedValue.value === APP_STRINGS.VALUE_RELEASE && !isClickupList && (
                <div className="min-w-[180px]">
                  <DropdownButton
                    buttonLabel={`${APP_STRINGS.LABEL_SELECT_PREFIX}${releaseLabel}`}
                    options={getAllReleaseList.map((release) => ({
                      value: release._id,
                      label: release.releaseName,
                      status: release.status,
                    }))}
                    onSelect={handleReleaseChange}
                    placeholder={APP_STRINGS.LABEL_SELECT_RELEASE}
                    selectedOption={selectedRelease.releaseName}
                    isOpen={isSprintOpen}
                    setIsOpen={setIsSprintOpen}
                    reference={sprintRef}
                    type={APP_STRINGS.API_RELEASE}
                    width="lg"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mt-4 ml-4">
              <div>
                {!isHoursBasedProject && (() => {
                  const currentProject = getAllProjectList.find(p => p._id === selectedProject.id);
                  return currentProject?.projectTypeKey !== "gitlab-project";
                })() && (
                  <div className="flex items-center text-sm gap-2">
                    <span className="text-gray-700 dark:text-[#FFFFFF] font-medium">
                          1 {isGithubIssues ? APP_STRINGS.ESTIMATE : 'Story Point'}
                    </span>
                    <input
                      type="number"
                      min={0}
                      value={storyPointToHourRatio}
                      onChange={(e) => setStoryPointToHourRatio(Number(e.target.value))}
                      className="w-14 px-1 py-[2px] h-[32px] text-sm border bg-white dark:bg-[#182433] text-custom-black dark:text-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-blue-400 rounded-md"
                    />
                    <span className="text-gray-700 dark:text-[#FFFFFF]">hrs</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={openAddUsersModal}
                  className={`${
                    !selectedProject.id || !isEditingAllowed
                      ? 'bg-gray-500 cursor-not-allowed'
                      : 'bg-[#22C55E] hover:bg-[#16A34A]'
                  } text-white px-4 py-2 rounded flex items-center`}
                  disabled={!selectedProject.id || !isEditingAllowed}
                >
                  Add Users
                </button>
                <button
                  onClick={saveCapacityData}
                  className={`${
                    !selectedProject.id || saveStatus || !isEditingAllowed
                      ? 'bg-gray-500 cursor-not-allowed'
                      : theme === 'light'
                        ? 'bg-[#24527A] hover:bg-[#5580A6]'
                        : 'bg-[#066FD1] hover:bg-[#2B8AE3]'
                  } text-white px-4 py-2 rounded flex items-center`}
                  disabled={!selectedProject.id || saveStatus || !isEditingAllowed}
                >
                  {saveStatus ? 'Saved' : 'Save Data'}
                </button>
              </div>
            </div>
            <div className="rounded-lg shadow-xl mt-4" style={{ height: "calc(100vh - 220px)" }}>
              <div className={`ml-3 pb-2 dark:bg-[#182433] dark:text-[#C8C8C8] ${theme === 'light' ? 'bg-[#FFFFFF] text-[#202020] border-2 border-[#DFE0EF]' : 'text-black border-2 border-[#25384F]'} rounded-lg shadow-md w-full flex-1 overflow-x-auto`}>
                <div className="capacity-planning-grid">
                  {!loading && (
                  <AGrid
                    key={selectedValue}
                    rowData={memoizedTeamData}
                    gridOptions={capacityPlanningGridOptions}
                    columnDefs={enhancedColumns}
                    defaultColDef={{
                      sortable: true,
                      sortIcon: true,
                      sortingOrder: ['asc', 'desc'],
                      resizable: true,
                      flex: 1,
                      tooltipShowDelay: 0,
                      filter: false,
                      floatingFilter: false,
                    }}
                    onFilterChanged={() => {
                      if (gridApiRef.current) gridApiRef.current.onFilterChanged();
                    }}
                    onGridReady={onGridReady}
                    onApiReady={(api) => {
                      gridApiRef.current = api;
                    }}
                    context={{
                      handleInputChange,
                      isEditingAllowed,
                    }}
                    getRowId={(params) => params.data.id}
                    tooltipShowDelay={100}
                    tooltipMouseTrack
                    domLayout="normal"
                    height={gridHeight}
                    initialPageSize={pageSize}
                    theme={theme}
                  />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <AddCapacityUsersModal
          isOpen={isAddUsersModalOpen}
          onClose={() => setIsAddUsersModalOpen(false)}
          onApply={applyAddUsersModal}
          isPersistAllowed={isEditingAllowed}
          recalledOffPlan={modalRecalledOffPlan}
          purgedManualNormKeys={purgedOffPlanManualNorms}
          teamData={teamData}
          sprintReleaseAssignees={addModalSprintReleaseAssignees}
          isHoursBasedProject={isHoursBasedProject}
          storyPointToHourRatio={storyPointToHourRatio}
          calculateDependentValues={calculateDependentValues}
          roles={roles}
          onRequestRemoveManualUser={requestRemoveManualUserFromPlan}
        />
        {manualDeleteConfirmRowId != null && (
          <Modal
            isOpen
            onClose={() => setManualDeleteConfirmRowId(null)}
            title="Remove user"
            size="small"
            content={
              <div className="flex flex-col items-center justify-center py-4 px-4 text-center">
                <p
                  className={`text-sm mb-6 ${
                    theme === 'light' ? 'text-[#073C6A]' : 'text-gray-300'
                  }`}
                >
                  Are you sure you want to remove this user from the capacity plan?
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    type="button"
                    onClick={() => setManualDeleteConfirmRowId(null)}
                    className={`px-4 py-2 rounded text-sm font-medium ${
                      theme === 'light'
                        ? 'bg-gray-200 text-[#202020] hover:bg-gray-300'
                        : 'bg-[#25384F] text-[#D9E4F1] hover:bg-[#2f4a63]'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={confirmRemoveManualUserFromPlan}
                    className="px-4 py-2 rounded text-sm font-medium bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            }
          />
        )}
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title=""
          className="max-w-sm"
          content={
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="mb-3">
                <div className="h-10 w-10 rounded-full bg-green-500 bg-opacity-20 flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <h2 className="text-lg font-semibold mb-1 dark:text-white text-[#0A2342]">Success</h2>
              <p className={`text-sm mb-4 ${theme === 'light' ? 'text-[#0A2342]' : 'text-gray-300'}`}>Data saved successfully!</p>
              <button
                onClick={() => setIsModalOpen(false)}
                className="mt-2 px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition"
              >
                OK
              </button>
            </div>
          }
        />
        {warningModal.isOpen && (
          <Modal
            isOpen={warningModal.isOpen}
            onClose={() => setWarningModal({ isOpen: false, message: '' })}
            title="Warning"
            className="max-w-sm"
            content={
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="mb-3">
                  <div className="h-10 w-10 rounded-full bg-yellow-500 bg-opacity-20 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                      />
                    </svg>
                  </div>
                </div>
                <h2 className="text-lg font-semibold mb-1 text-white">Warning</h2>
                <p className={`text-sm mb-4 ${theme === 'light' ? 'text-[#073C6A]' : 'text-gray-300'}`}>{warningModal.message}</p>
                <button
                  onClick={() => setWarningModal({ isOpen: false, message: '' })}
                  className="mt-2 px-3 py-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition"
                >
                  OK
                </button>
              </div>
            }
          />
        )}
      </>
    </CommonLayout>
  );
};

export default CapacityDashboard;